import { FieldTypeId } from "@models/configuration";
import { SelectedSubProductDTO } from "@models/managed-accounts/entities/step/fee";
import {
  AnalysisDTO,
  AnalysisUserType,
} from "@models/platform-analysis/entities/analysis";
import { DisclaimerDTO } from "@models/platform-analysis/entities/disclaimer";
import { AnalysisStepDTO } from "@models/platform-analysis/entities/step";
import {
  AnalysisFeatureAllDataDTO,
  AnalysisFeatureIncludeDTO,
  FeatureAnalysisRowDTO,
  ImageDTO,
  SelectedProductDTO,
} from "@models/platform-analysis/entities/steps/feature";
import {
  FeeDetailDTO,
  FeeDTO,
  FeeSubProductDTO,
} from "@models/platform-analysis/entities/steps/fee";
import {
  PDFAnalysisComment,
  PDFCommentDetail,
  PDFHtmlElement,
  PDFPageHandlerProps,
  PDFPageTemplate,
  PDFReviewCommentSeparated,
  PortfolioCompositionRowDTO,
  PortfolioCompositionSectionDTO,
  PortfolioCompositionSetupDTO,
  ProductRecommendationDTO,
  SummaryCommentSettingDto,
} from "@models/platform-analysis/entities/steps/summary";
import { FeesDisplayStyle } from "@models/platform-analysis/enums/fee/displayStyle";
import { FeesPerformanceScope } from "@models/platform-analysis/enums/fee/feesPerformanceScope";
import { FeeHeatmapMode } from "@models/platform-analysis/enums/fee/heatmapMode";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { ProductRecommendationTypes } from "@models/platform-analysis/enums/summary/recommendationType";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import {
  getStringFamilyMemberAccounts,
  removeSubProductNameForSpecificProduct,
  sortByProductOrder,
  sortBySubProductOrder,
  sortFeatureAnalysisRowsByProductOrder,
} from "@pages/platform-analysis-v2/_id/utils";
import { PageTemplate } from "@pages/platform-analysis/components/buttons/export-pdf/model";
import { View } from "@react-pdf/renderer";
import {
  compileFeatureAnalysisRows,
  splitFeatureAnalysis,
} from "@redux/slices/platform-analysis/util";
import { formatNumberFixed, thousandSeparator } from "@utils";
import { getHSLLightness, hslToHex } from "@utils/color";
import {
  chunk,
  cloneDeep,
  defaultTo,
  groupBy,
  isEmpty,
  isEqual,
  isNil,
  join,
  map,
} from "lodash";
import {
  FeeAnalysisLabelConstants,
  LineChartLegendConstants,
} from "../../../fee/analysis/constants";
import { getProductRecommendationConfigs } from "../../utils";
import { PDFAdviserInformation } from "../adviser-information/pdf";
import { PDFAppendix } from "../appendix/pdf";
import { PDFBusinessMetrics } from "../business-metrics/pdf";
import { PDFDisclaimer } from "../disclaimers/pdf";
import { PDFFeaturesIncluded } from "../features-included/pdf";
import { PDFFeatureAnalysis } from "../features/pdf";
import { PDFFeeAnalysisHeatMap } from "../fee-estimates/pdf/heat-map";
import { PDFFeeLineChart } from "../fee-estimates/pdf/line-chart";
import { PDFObjectives } from "../objective/pdf";
import { PDFPlatformSpecificComments } from "../platform-specific-comments/pdf";
import { PDFPlatformIncluded } from "../platforms-included/pdf";
import { PDFPortfolioComposition } from "../portfolio-composition/pdf";
import { PDFFeePortfolioDetails } from "../portfolio-details/pdf";
import { getAnalysisSummaryPortfolioDetailTableRows } from "../portfolio-details/utils";
import { PDFSMAPortfolio } from "../sma-portfolio-fees/pdf";
import { PDFSupplierInformation } from "../supplier-information/pdf";
import { PDFWhatsIncluded } from "../whats-included/pdf";
import { PDFFeeLineChartSubProduct } from "./components/line-chart-sub-product";
import { PDFTitle } from "./components/title";
import {
  PDFFeatureSectionIconPrefix,
  PDFSectionRenderConfigs,
  PDFStyleConfigs,
} from "./configs";

export const checkContainsMultipleLinesRecommendationBadge = <
  T extends { recommendation?: Partial<ProductRecommendationDTO> },
>({
  selectedProducts,
  productRecommendations,
}: {
  selectedProducts?: T[];
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
}) => {
  return !!(
    shouldShowRecommendationLabel(productRecommendations) &&
    selectedProducts?.some((product) => {
      const recommendationType = product.recommendation?.recommendationType;
      if (recommendationType !== ProductRecommendationTypes.Custom) {
        return false;
      }

      const labelLength =
        getProductRecommendationConfigs(product.recommendation).label?.length ||
        0;

      return labelLength > PDFStyleConfigs.TableCellMaxCharsPerLine;
    })
  );
};

export const generateFeatureSectionIconUrl = (iconUrl: string) => {
  const urlSplit = iconUrl.split("/").pop();
  if (!isNil(urlSplit)) {
    const fileName = urlSplit.substring(0, urlSplit.lastIndexOf("."));

    return PDFFeatureSectionIconPrefix + fileName + ".png";
  }

  return "";
};

export const pagesHandler = (
  pages: PDFPageTemplate[],
  component: JSX.Element,
  isAppend?: boolean,
) => {
  if (isAppend) {
    const lastPage = pages[pages?.length - 1];
    lastPage?.components?.push(<View style={{ marginTop: 15 }}></View>);
    lastPage?.components?.push(component);
  } else {
    pages?.push({
      components: [component],
    });
  }
};

export const generatePDFImageIconUrl = (iconUrl?: string) => {
  let result = iconUrl;

  if (
    process.env.REACT_APP_AUTH0_REDIRECT_URL ===
      "https://app.dev.suitabilityhub.com" ||
    process.env.REACT_APP_AUTH0_REDIRECT_URL ===
      "https://local-app.suitabilityhub.com:3000"
  )
    result = result?.replace(
      "https://sh-dev-s3-bucket-public-apse2.s3.ap-southeast-2.amazonaws.com",
      process.env.REACT_APP_AUTH0_REDIRECT_URL,
    );

  if (
    process.env.REACT_APP_AUTH0_REDIRECT_URL ===
    "https://app.staging.suitabilityhub.com"
  )
    result = result?.replace(
      "https://sh-staging-s3-bucket-public-apse2.s3.ap-southeast-2.amazonaws.com",
      process.env.REACT_APP_AUTH0_REDIRECT_URL,
    );

  if (
    process.env.REACT_APP_AUTH0_REDIRECT_URL ===
    "https://app.prod.suitabilityhub.com"
  )
    result = result?.replace(
      "https://sh-prod-s3-bucket-public-apse2.s3.ap-southeast-2.amazonaws.com",
      process.env.REACT_APP_AUTH0_REDIRECT_URL,
    );

  return result;
};

export const chunkSubstr = (str: string, size: number) => {
  const numChunks = Math.ceil(str?.length / size);
  const chunks = new Array(numChunks);

  for (let i = 0, o = 0; i < numChunks; ++i, o += size) {
    chunks[i] = str?.substring(o, size);
  }

  return chunks;
};

export const parseCommentProductLogo = (logo?: string | null) => {
  if (!logo) return;
  if (logo[0] !== "{") return logo;
  try {
    return (JSON.parse(logo) as ImageDTO).url;
  } catch (e) {
    return logo;
  }
};

export const shouldShowRecommendationLabel = (
  recommendations?: Record<string, Partial<ProductRecommendationDTO>>,
) => {
  if (!recommendations) return false;

  return Object.values(recommendations).some(
    (recommendation) => !!recommendation.recommendationType,
  );
};

export const checkHeadersContainsLongProductName = (
  selectedProducts?: SelectedProductDTO[],
) => {
  return !!selectedProducts?.some((product) => {
    const { productName } = product;

    if (!productName) return false;

    const isMoreThan3Lines = productName
      .split(" ")
      ?.some(
        (word) =>
          word.length > (PDFStyleConfigs.TableCellMaxCharsPerLine * 2) / 3,
      );
    const isLongName =
      productName.length > PDFStyleConfigs.TableCellMaxCharsPerLine * 2;

    return isMoreThan3Lines || isLongName;
  });
};

export const displayCurrencyNumber = (num?: number): string => {
  const value = num || 0;
  if (!value) return "";

  return `$${thousandSeparator(
    parseFloat(formatNumberFixed(value, value < Math.pow(10, 6) ? 1 : 2)),
  )}`;
};

export const calculateFeeDetailColumnWidth = (
  totalWidth: number,
  feeDetails: FeeDetailDTO[],
) => {
  const totalColumns = feeDetails.length;
  const columnWidth = Number(totalWidth / totalColumns);

  const hasTooManyFeeColumns =
    feeDetails.length >
    PDFStyleConfigs.FeeAnalysisMaxFeeDetailsColumnToDisplayLogo;
  const hasPYSColumn = !!feeDetails.find(
    (feeDetail) => feeDetail.name === FeeAnalysisLabelConstants.PYSRefund,
  );

  if (hasTooManyFeeColumns && hasPYSColumn) {
    const pysColumnWidth = columnWidth * (2 / 3);
    const otherColumnWidth = (totalWidth - pysColumnWidth) / (totalColumns - 1);

    return { pysColumnWidth: Math.floor(pysColumnWidth), otherColumnWidth };
  }

  return { pysColumnWidth: columnWidth, otherColumnWidth: columnWidth };
};

export const getYAxisTicks = (highestFee: number): number[] => {
  // if the highest fee is 0 or +/-Infinity, return an array containing only 0
  if (highestFee === 0 || highestFee === -Infinity || highestFee === Infinity)
    return [0];
  // calculate the tick interval by dividing the highest fee by 7
  // (instead of 6, as there are 8 ticks)
  const tickInterval = highestFee / 7;
  // calculate the tick value using the `getTickNumber` function
  const tickValue = getTickNumber(tickInterval);
  // initialize the ticks array with 0 as the first tick
  const ticks: number[] = [];
  ticks.push(0);
  let i = 0;
  // generate ticks based on the tick value
  if (tickInterval > 0) {
    do {
      i = i + tickValue;
      ticks.push(i);
    } while (i < highestFee);
  } else {
    do {
      i = i - tickValue;
      ticks.unshift(i);
    } while (i >= highestFee);
  }

  // return the array of ticks
  return ticks;
};

export const getFeeMinMaxExcludePerformanceFees = ({
  subProducts,
  isExcludePerformanceFees = false,
  isDollarMode = true,
}: {
  subProducts?: FeeSubProductDTO[];
  isExcludePerformanceFees?: boolean;
  isDollarMode?: boolean;
}) => {
  let min = Infinity;
  let max = -Infinity;

  if (!subProducts) {
    return { min, max };
  }

  const dollarKey: keyof FeeDTO = isExcludePerformanceFees
    ? "totalCostExcPerfForDisplay"
    : "totalCostForDisplay";
  const fallBackDollarKey: keyof FeeDTO = "totalCostForDisplay";

  const percentageKey: keyof FeeDTO = isExcludePerformanceFees
    ? "totalCostExcPerfForDisplayPercentage"
    : "totalCostForDisplayPercentage";
  const fallBackPercentageKey: keyof FeeDTO = "totalCostForDisplayPercentage";

  const dataKey = isDollarMode ? dollarKey : percentageKey;
  const fallBackDataKey = isDollarMode
    ? fallBackDollarKey
    : fallBackPercentageKey;

  subProducts.forEach((subProduct) => {
    subProduct.fees.forEach((fee) => {
      min = Math.min(min, fee[dataKey] || fee[fallBackDataKey] || 0);
      max = Math.max(max, fee[dataKey] || fee[fallBackDataKey] || 0);
    });
  });

  return { min, max };
};

export const generatePDFPages = ({
  userType,
  analysis,
  analysisSteps,
  productRecommendations,
  hiddenIdsIndexMap,
  disqualifiedHiddenIdsIndexMap,
  hiddenIds,
  disqualifiedHiddenIds,
  feeSubProducts,
  disqualifiedFeeSubProducts,
  featureAnalysisTableRows,
  businessMetricTableRows,
  disclaimers,
  smaTableRows,
  disqualifiedSmaTableRows,
}: {
  userType: AnalysisUserType;
  analysis: AnalysisDTO;
  analysisSteps?: AnalysisStepDTO;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  hiddenIdsIndexMap?: boolean[];
  disqualifiedHiddenIdsIndexMap?: boolean[];
  hiddenIds?: string[];
  disqualifiedHiddenIds?: string[];
  feeSubProducts?: FeeSubProductDTO[];
  disqualifiedFeeSubProducts?: FeeSubProductDTO[];
  featureAnalysisTableRows?: FeatureAnalysisRowDTO[];
  businessMetricTableRows?: FeatureAnalysisRowDTO[];
  disclaimers?: DisclaimerDTO[];
  smaTableRows?: FeatureAnalysisRowDTO[];
  disqualifiedSmaTableRows?: FeatureAnalysisRowDTO[];
}) => {
  const pages: PDFPageTemplate[] = [];
  let spaceAvailable = analysis.isReview
    ? PDFStyleConfigs.ReviewPageBodyHeight
    : PDFStyleConfigs.PageBodyHeight;

  PDFSectionRenderConfigs.forEach((config) => {
    if (!config) return;

    const { showFor, pdfPageHandler, name, pdfTitle, isAppendix } = config;

    if (!showFor.includes(userType) || isNil(pdfPageHandler)) return null;

    spaceAvailable = pdfPageHandler({
      pages: pages,
      spaceAvailable: spaceAvailable,
      analysis: analysis,
      title: pdfTitle?.[userType] ?? name?.[userType],
      analysisSteps: analysisSteps,
      isAppendix: isAppendix,
      productRecommendations: productRecommendations,
      hiddenIdsIndexMap: hiddenIdsIndexMap,
      disqualifiedHiddenIdsIndexMap: disqualifiedHiddenIdsIndexMap,
      hiddenIds: hiddenIds,
      disqualifiedHiddenIds: disqualifiedHiddenIds,
      feeSubProducts: feeSubProducts,
      disqualifiedFeeSubProducts: disqualifiedFeeSubProducts,
      featureAnalysisTableRows: featureAnalysisTableRows ?? [],
      businessMetricTableRows: businessMetricTableRows ?? [],
      disclaimers: disclaimers ?? [],
      smaTableRows: smaTableRows,
      disqualifiedSmaTableRows: disqualifiedSmaTableRows,
    });
  });

  return pages;
};

export const supplierInformationPageHandler = ({
  pages,
  analysis,
}: PDFPageHandlerProps) => {
  pagesHandler(
    pages,
    <View key={pages?.length} style={{ gap: PDFStyleConfigs.GapLarge }}>
      <PDFSupplierInformation analysis={analysis} />
    </View>,
  );

  return 0;
};

export const adviserInformationPageHandler = ({
  pages,
  analysis,
}: PDFPageHandlerProps) => {
  pagesHandler(
    pages,
    <View key={pages?.length} style={{ gap: PDFStyleConfigs.GapLarge }}>
      <PDFAdviserInformation analysis={analysis} />
    </View>,
  );

  return 0;
};

export const objectivesPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  title,
}: PDFPageHandlerProps) => {
  const isIncludeMainHeader =
    pages.length <= PDFStyleConfigs.MaxPageIndexToShowMainHeader;
  const pageBodyHeight = analysis.isReview
    ? isIncludeMainHeader
      ? PDFStyleConfigs.ReviewPageBodyHeight
      : PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
    : isIncludeMainHeader
    ? PDFStyleConfigs.PageBodyHeight
    : PDFStyleConfigs.PageBodyHeightWithoutHeading;
  const isAppend = spaceAvailable > pageBodyHeight / 2;
  spaceAvailable =
    (isAppend ? spaceAvailable : pageBodyHeight) - PDFStyleConfigs.TitleHeight;

  const objectiveNodeCreated = createElementNode(
    expandHTMLTagInPDF(analysis.summary?.objectives ?? ""),
  );
  const objectiveSeparated = parseToHTMLElementsByNode(
    objectiveNodeCreated,
    `${PDFStyleConfigs.ContentWidth}px`,
    spaceAvailable,
    false,
  );
  const objectivesArray = objectiveSeparated.elements ?? [];
  let isUpdateAvailablePage = false;
  objectivesArray.forEach((objective, index) => {
    if (isAppend && !isUpdateAvailablePage) {
      pagesHandler(
        pages,
        <PDFObjectives
          key={index}
          title={title}
          content={objective?.content}
          isContinue={index > 0}
        />,
        true,
      );
      isUpdateAvailablePage = true;
    } else {
      pagesHandler(
        pages,
        <PDFObjectives
          key={index}
          title={title}
          content={objective?.content}
          isContinue={index > 0}
        />,
        false,
      );
    }

    if (index === objectivesArray?.length - 1) {
      if (objectivesArray?.length > 1)
        spaceAvailable = pageBodyHeight - PDFStyleConfigs.TitleHeight;
      spaceAvailable -= objective?.height ?? 0;
    }
  });

  return spaceAvailable - PDFStyleConfigs.MarginDefault;
};

export const whatsIncludedPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  title,
  analysisSteps,
}: PDFPageHandlerProps) => {
  const description = getWhatsIncludedDescription(analysis, analysisSteps);
  const lineCount = Math.ceil(
    description.length / PDFStyleConfigs.MaxSingleLineCharacters,
  );
  const whatsIncludedHeight =
    lineCount * PDFStyleConfigs.ContentSingleLineHeight;
  const isAppend = spaceAvailable > whatsIncludedHeight && pages.length > 1;

  pagesHandler(
    pages,
    <PDFWhatsIncluded
      title={title}
      description={description}
    ></PDFWhatsIncluded>,
    isAppend,
  );

  return spaceAvailable - whatsIncludedHeight - PDFStyleConfigs.MarginDefault;
};

export const platformSpecificCommentsPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  productRecommendations,
  analysisSteps,
  isAppendix,
}: PDFPageHandlerProps) => {
  const isReview = analysis.isReview;
  const pageBodyHeight = isReview
    ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
    : PDFStyleConfigs.PageBodyHeightWithoutHeading;
  const isAppend = spaceAvailable > pageBodyHeight / 2;
  const additionalHeight =
    (isReview && isAppendix
      ? PDFStyleConfigs.AppendixCommentTableHeaderHeight
      : PDFStyleConfigs.CommentTableHeaderHeight) +
    PDFStyleConfigs.TitleHeight +
    10;

  spaceAvailable =
    (isAppend ? spaceAvailable : pageBodyHeight) - additionalHeight;

  const comments =
    analysis.summary?.analysisComments
      ?.map((comment) => {
        return {
          ...comment,
          feature: expandHTMLTagInPDF(comment?.feature ?? ""),
          businessMetric: expandHTMLTagInPDF(comment?.businessMetric ?? ""),
          fee: expandHTMLTagInPDF(comment?.fee ?? ""),
          summary: expandHTMLTagInPDF(comment?.summary ?? ""),
        } as PDFAnalysisComment;
      })
      .filter((comment) =>
        isAppendix ? !comment.inFinalShortlist : comment.inFinalShortlist,
      )
      .sort(
        (a, b) =>
          (productRecommendations?.[a.productId]?.order ??
            Number.MAX_SAFE_INTEGER) -
          (productRecommendations?.[b.productId]?.order ??
            Number.MAX_SAFE_INTEGER),
      ) ?? [];
  const specificCommentSetting = analysis.summary?.specificCommentSetting;

  const extractedComments = extractReviewComments(
    comments,
    spaceAvailable,
    false,
    analysisSteps,
    specificCommentSetting,
  );
  let breakComments: PDFAnalysisComment[] = [];
  let isUpdateAvailablePage = false;
  let isExpandTable = false;

  if (
    specificCommentSetting?.showBusinessMetric ||
    specificCommentSetting?.showFeature ||
    specificCommentSetting?.showFee ||
    specificCommentSetting?.showSummary
  ) {
    const setPageValues = () => {
      if (isAppend && !isUpdateAvailablePage) {
        pagesHandler(
          pages,
          <PDFPlatformSpecificComments
            comments={breakComments ?? []}
            commentSetting={specificCommentSetting}
            productRecommendations={productRecommendations}
            isAppendix={isAppendix}
            isExpandTable={isExpandTable}
            analysisStep={analysisSteps}
            isReview={isReview}
          />,
          true,
        );
        isUpdateAvailablePage = true;
      } else {
        pagesHandler(
          pages,
          <PDFPlatformSpecificComments
            comments={breakComments ?? []}
            commentSetting={specificCommentSetting}
            productRecommendations={productRecommendations}
            isAppendix={isAppendix}
            isExpandTable={isExpandTable}
            analysisStep={analysisSteps}
            isReview={isReview}
          />,
          false,
        );
      }
    };

    extractedComments?.forEach((comment, index) => {
      if (comment.height < spaceAvailable) {
        breakComments.push(comment);
        spaceAvailable -= comment.height;
      } else {
        setPageValues();
        breakComments = [comment];
        isExpandTable = true;
        spaceAvailable = pageBodyHeight - additionalHeight - comment.height;
      }

      if (index === extractedComments.length - 1) {
        setPageValues();
      }
    });
  }

  return spaceAvailable - (isAppend ? PDFStyleConfigs.MarginDefault : 0);
};

export const featurePageHandler = ({
  pages,
  analysis,
  spaceAvailable,
  title,
  productRecommendations,
  featureAnalysisTableRows,
}: PDFPageHandlerProps) => {
  if (!analysis.summary?.hasFeatureStep) return spaceAvailable;

  const showSubSection =
    analysis.summary?.featureAnalysisTableViewMode !== TableViewMode.Summary;
  const isDetailMode =
    analysis.summary?.featureAnalysisTableViewMode === TableViewMode.Detail;
  const showAnalysisScore = !!analysis.summary?.showAnalysisScore;
  const selectedProducts =
    analysis.summary?.featureAnalysis?.shortlisted?.selectedProducts;

  const isIncludeMainHeader =
    pages.length <= PDFStyleConfigs.MaxPageIndexToShowMainHeader;
  const pageBodyHeight = analysis.isReview
    ? isIncludeMainHeader
      ? PDFStyleConfigs.ReviewPageBodyHeight
      : PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
    : isIncludeMainHeader
    ? PDFStyleConfigs.PageBodyHeight
    : PDFStyleConfigs.PageBodyHeightWithoutHeading;
  const isAppend =
    spaceAvailable > pageBodyHeight / 2 &&
    spaceAvailable !== pageBodyHeight &&
    pages.length > 1;

  spaceAvailable = isAppend ? spaceAvailable : pageBodyHeight;

  const sortedSelectedProducts = sortByProductOrder({
    data: selectedProducts ?? [],
    productRecommendations: productRecommendations,
    getItemId: (item) => item.id,
  });

  const containsLongProductName = checkHeadersContainsLongProductName(
    sortedSelectedProducts,
  );

  const renderedComponent = ({
    rows,
    isExpand,
  }: {
    rows: FeatureAnalysisRowDTO[];
    isExpand: boolean;
  }) => (
    <PDFFeatureAnalysis
      selectedProducts={sortedSelectedProducts}
      tableRows={rows}
      productRecommendations={productRecommendations}
      title={title}
      tableViewMode={analysis.summary?.featureAnalysisTableViewMode}
      isExpand={isExpand}
      isReview={analysis.isReview}
      hideScore={!showAnalysisScore}
    />
  );

  const shouldShowRecommendation = shouldShowRecommendationLabel(
    productRecommendations,
  );

  const filteredRowsByViewMode = featureAnalysisTableRows?.map((row) => ({
    ...row,
    subRows: showSubSection
      ? row.subRows?.map((subRow) => ({
          ...subRow,
          subRows: isDetailMode ? subRow.subRows : [],
        }))
      : [],
  }));

  const sortedFeatureAnalysisTableRows = sortFeatureAnalysisRowsByProductOrder({
    originalRows: filteredRowsByViewMode ?? [],
    productRecommendations,
  });

  const flattenedRows: FeatureAnalysisRowDTO[] = [];
  sortedFeatureAnalysisTableRows.forEach((row) => {
    flattenedRows.push({ ...row, subRows: [] });
    if (row.subRows && row.subRows.length > 0) {
      row.subRows.forEach((subRow) => {
        flattenedRows.push({ ...subRow, subRows: [] });
        if (subRow.subRows && subRow.subRows.length > 0) {
          subRow.subRows.forEach((subSubRow) => {
            flattenedRows.push({ ...subSubRow, subRows: [] });
          });
        }
      });
    }
  });

  let sectionProcessed = processFeatureAnalysisSectionData({
    spaceAvailable,
    rows: flattenedRows,
    isFeature: true,
    shouldShowRecommendationLabel: shouldShowRecommendation,
    containsLongProductName,
  });

  pagesHandler(
    pages,
    renderedComponent({ rows: sectionProcessed.data, isExpand: false }),
    isAppend,
  );

  while (sectionProcessed.hasContinue && !isEmpty(flattenedRows)) {
    spaceAvailable = analysis.isReview
      ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
      : PDFStyleConfigs.PageBodyHeightWithoutHeading;

    sectionProcessed = processFeatureAnalysisSectionData({
      spaceAvailable,
      rows: flattenedRows,
      shouldShowRecommendationLabel: shouldShowRecommendation,
    });

    pagesHandler(
      pages,
      renderedComponent({ rows: sectionProcessed.data, isExpand: true }),
      false,
    );
  }

  return (
    spaceAvailable -
    sectionProcessed.occupyHeight -
    PDFStyleConfigs.MarginDefault
  );
};

export const businessMetricsPageHandler = ({
  pages,
  analysis,
  spaceAvailable,
  title,
  productRecommendations,
  businessMetricTableRows,
}: PDFPageHandlerProps) => {
  if (!analysis.summary?.hasBusinessMetricStep) return spaceAvailable;

  const selectedProducts =
    analysis.summary?.businessMetricAnalysis?.shortlisted?.selectedProducts;
  const isIncludeMainHeader =
    pages.length <= PDFStyleConfigs.MaxPageIndexToShowMainHeader;
  const pageBodyHeight = analysis.isReview
    ? isIncludeMainHeader
      ? PDFStyleConfigs.ReviewPageBodyHeight
      : PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
    : isIncludeMainHeader
    ? PDFStyleConfigs.PageBodyHeight
    : PDFStyleConfigs.PageBodyHeightWithoutHeading;
  const isAppend =
    spaceAvailable > pageBodyHeight / 2 &&
    spaceAvailable !== pageBodyHeight &&
    pages.length > 1;

  spaceAvailable = isAppend ? spaceAvailable : pageBodyHeight;

  const sortedSelectedProducts = sortByProductOrder({
    data: selectedProducts ?? [],
    productRecommendations: productRecommendations,
    getItemId: (item) => item.id,
  });

  const renderedComponent = ({
    rows,
    isExpand,
  }: {
    rows: FeatureAnalysisRowDTO[];
    isExpand: boolean;
  }) => (
    <PDFBusinessMetrics
      selectedProducts={sortedSelectedProducts}
      tableRows={rows}
      productRecommendations={productRecommendations}
      title={title}
      isExpand={isExpand}
      isReview={analysis.isReview}
      isBusinessMetric={true}
      tableViewMode={TableViewMode.Detail}
    />
  );

  const shouldShowRecommendation = shouldShowRecommendationLabel(
    productRecommendations,
  );

  const sortedFeatureAnalysisTableRows = sortFeatureAnalysisRowsByProductOrder({
    originalRows: businessMetricTableRows ?? [],
    productRecommendations,
  });

  const flattenedRows: FeatureAnalysisRowDTO[] = [];
  sortedFeatureAnalysisTableRows.forEach((row) => {
    flattenedRows.push({ ...row, subRows: [] });
    if (row.subRows && row.subRows.length > 0) {
      row.subRows.forEach((subRow) => {
        flattenedRows.push({ ...subRow, subRows: [] });
        if (subRow.subRows && subRow.subRows.length > 0) {
          subRow.subRows.forEach((subSubRow) => {
            flattenedRows.push({ ...subSubRow, subRows: [] });
          });
        }
      });
    }
  });

  let sectionProcessed = processFeatureAnalysisSectionData({
    spaceAvailable,
    rows: flattenedRows,
    isFeature: true,
    shouldShowRecommendationLabel: shouldShowRecommendation,
  });

  pagesHandler(
    pages,
    renderedComponent({ rows: sectionProcessed.data, isExpand: false }),
    isAppend,
  );

  while (sectionProcessed.hasContinue && !isEmpty(flattenedRows)) {
    spaceAvailable = analysis.isReview
      ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
      : PDFStyleConfigs.PageBodyHeightWithoutHeading;

    sectionProcessed = processFeatureAnalysisSectionData({
      spaceAvailable,
      rows: flattenedRows,
      shouldShowRecommendationLabel: shouldShowRecommendation,
    });

    pagesHandler(
      pages,
      renderedComponent({ rows: sectionProcessed.data, isExpand: true }),
      false,
    );
  }

  return (
    spaceAvailable -
    sectionProcessed.occupyHeight -
    PDFStyleConfigs.MarginDefault
  );
};

export const portfolioDetailPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  hiddenIds,
  feeSubProducts,
  title,
}: PDFPageHandlerProps) => {
  if (!analysis.summary?.hasFeeStep) return spaceAvailable;

  const isIncludeMainHeader =
    pages.length <= PDFStyleConfigs.MaxPageIndexToShowMainHeader;
  const pageBodyHeight = analysis.isReview
    ? isIncludeMainHeader
      ? PDFStyleConfigs.ReviewPageBodyHeight
      : PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
    : isIncludeMainHeader
    ? PDFStyleConfigs.PageBodyHeight
    : PDFStyleConfigs.PageBodyHeightWithoutHeading;

  const { portfolioSetup, isShowHoldingsNumber } =
    analysis.summary?.feeAnalysis?.shortlisted || {};
  const tableData = analysis.summary?.feePortfolioDetails;

  const familyMemberRows =
    tableData?.familyMembers?.map((member) => ({
      label: member.name || <></>,
      value: getStringFamilyMemberAccounts(member.familyMemberAccounts),
    })) || [];

  const tableRows = getAnalysisSummaryPortfolioDetailTableRows({
    data: tableData,
    selectedSubProducts: feeSubProducts,
    hiddenSubProductIds: hiddenIds,
    portfolioLevel: portfolioSetup,
    showHoldingsNumber: isShowHoldingsNumber,
    forPdf: true,
  });

  const displayRows = familyMemberRows.concat(tableRows);

  const getHeight = (str: string, maxCharsPerLine: number) =>
    Math.max(str.length / maxCharsPerLine, 1) *
    PDFStyleConfigs.TableRowSingleLine;

  const tableHeight =
    displayRows.reduce((total, row) => {
      const { label, value } = row;
      const valueLinesHeight = getHeight(
        value,
        PDFStyleConfigs.PortfolioDetailsValueColMaxChars,
      );
      const labelLinesHeight =
        typeof label === "string"
          ? getHeight(label, PDFStyleConfigs.PortfolioDetailsLabelColMaxChars)
          : PDFStyleConfigs.TableRowSingleLine * 2;

      return total + Math.max(valueLinesHeight, labelLinesHeight);
    }, 0) + PDFStyleConfigs.SubTitleHeight;

  const isAppend =
    spaceAvailable > pageBodyHeight / 2 &&
    spaceAvailable !== pageBodyHeight &&
    pages.length > 1 &&
    spaceAvailable > tableHeight;

  pagesHandler(
    pages,
    <PDFFeePortfolioDetails
      title={title}
      tableRows={displayRows}
      isPlatformAnalysis={!analysis.isReview}
    />,
    isAppend,
  );

  return spaceAvailable - tableHeight - PDFStyleConfigs.MarginDefault;
};

export const concludingRemarksPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  title,
}: PDFPageHandlerProps) => {
  const isIncludeMainHeader =
    pages.length <= PDFStyleConfigs.MaxPageIndexToShowMainHeader;
  const pageBodyHeight = analysis.isReview
    ? PDFStyleConfigs.ReviewPageBodyHeight
    : isIncludeMainHeader
    ? PDFStyleConfigs.PageBodyHeight
    : PDFStyleConfigs.PageBodyHeightWithoutHeading;

  const isAppend = spaceAvailable > pageBodyHeight / 2;
  spaceAvailable =
    (isAppend ? spaceAvailable : pageBodyHeight) - PDFStyleConfigs.TitleHeight;

  const concludingRemarkNodeCreated = createElementNode(
    expandHTMLTagInPDF(analysis.summary?.concludingRemarks ?? ""),
  );
  const concludingRemarkSeparated = parseToHTMLElementsByNode(
    concludingRemarkNodeCreated,
    `${PDFStyleConfigs.ContentWidth}px`,
    spaceAvailable,
    false,
  );
  const concludingRemarkArray = concludingRemarkSeparated.elements ?? [];
  let isUpdateAvailablePage = false;

  concludingRemarkArray.forEach((objective, index) => {
    if (isAppend && !isUpdateAvailablePage) {
      pagesHandler(
        pages,
        <PDFObjectives
          key={index}
          title={title}
          content={objective?.content}
          isContinue={index > 0}
        />,
        true,
      );
      isUpdateAvailablePage = true;
    } else {
      pagesHandler(
        pages,
        <PDFObjectives
          key={index}
          title={title}
          content={objective?.content}
          isContinue={index > 0}
        />,
        false,
      );
    }

    if (index === concludingRemarkArray?.length - 1) {
      if (concludingRemarkArray?.length > 1)
        spaceAvailable = pageBodyHeight - PDFStyleConfigs.TitleHeight;
      spaceAvailable -= objective?.height ?? 0;
    }
  });

  return spaceAvailable - PDFStyleConfigs.MarginDefault;
};

export const platformIncludedPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
}: PDFPageHandlerProps) => {
  const pageBodyHeight = PDFStyleConfigs.PageBodyHeightWithoutHeading;

  const platformDisplayed = join(
    map(analysis.summary?.selectedProducts, "name"),
    ", ",
  );

  const lineCount = Math.ceil(
    platformDisplayed.length / PDFStyleConfigs.MaxSingleLineCharacters,
  );
  const platformIncludedHeight =
    lineCount * PDFStyleConfigs.ContentSingleLineHeight;
  const isAppend = spaceAvailable > platformIncludedHeight;

  if (!isAppend) spaceAvailable = pageBodyHeight;
  spaceAvailable -= platformIncludedHeight;

  const platformSelectedSectionComp = (
    <PDFPlatformIncluded platformDisplayed={platformDisplayed} />
  );

  pagesHandler(pages, platformSelectedSectionComp, isAppend);

  return spaceAvailable - PDFStyleConfigs.MarginDefault;
};

export const disclaimersPageHandler = ({
  pages,
  analysis,
  disclaimers,
}: PDFPageHandlerProps) => {
  const hasFeeStep = analysis?.summary?.hasFeeStep;

  pagesHandler(
    pages,
    <PDFDisclaimer hasFeeStep={hasFeeStep} disclaimers={disclaimers} />,
    false,
  );

  return 0;
};

export const featureIncludedPageHandler = ({
  pages,
  analysis,
  spaceAvailable,
}: PDFPageHandlerProps) => {
  if (!analysis.summary?.showSelectedFeatures) return spaceAvailable;

  const pageBodyHeight = PDFStyleConfigs.PageBodyHeightWithoutHeading;
  const isAppend = spaceAvailable > pageBodyHeight / 2;

  spaceAvailable = isAppend ? spaceAvailable : pageBodyHeight;

  const featureIncludedComp = (
    dataSeparated: AnalysisFeatureIncludeDTO[],
    isContinued?: boolean,
  ) => (
    <View>
      <PDFTitle
        title={`Features included in the${
          analysis.isReview ? " review" : " analysis"
        }${isContinued ? " (continued)" : ""}`}
      />
      <PDFFeaturesIncluded featureIncluded={dataSeparated} />
    </View>
  );

  const selectedFeatures = cloneDeep(analysis.summary?.selectedFeatures) ?? [];
  let dataProcessed = processFeatureIncluded(spaceAvailable, selectedFeatures);
  pagesHandler(
    pages,
    featureIncludedComp(dataProcessed?.dataSeparated),
    isAppend,
  );

  while (dataProcessed.hasContinue) {
    spaceAvailable = pageBodyHeight;
    dataProcessed = processFeatureIncluded(spaceAvailable, selectedFeatures);
    pagesHandler(
      pages,
      featureIncludedComp(dataProcessed?.dataSeparated, true),
      false,
    );
  }

  return (
    spaceAvailable - dataProcessed.occupyHeight - PDFStyleConfigs.MarginDefault
  );
};

export const portfolioCompositionPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  productRecommendations,
  hiddenIdsIndexMap,
}: PDFPageHandlerProps) => {
  const hasFeeStep = analysis.summary?.hasFeeStep;
  if (!hasFeeStep) return spaceAvailable;

  let spaceAvailableAfterAppend = spaceAvailable;
  const numOfHiddenProducts = hiddenIdsIndexMap?.filter(Boolean)?.length ?? 0;

  const itemProductIdsMap: Record<string, string> = {};
  analysis?.summary?.smaPortfolioFee?.selectedSubProducts?.forEach((item) => {
    const key = `${item.id}-${item.rateCardVersion ?? 0}`;
    itemProductIdsMap[key] = item.productId;
  });

  const mappedSetups =
    analysis.summary?.portfolioCompositionByPlatform?.setups?.map((setup) => ({
      ...setup,
      productId: itemProductIdsMap[`${setup.id}-${setup.rateCardVersion ?? 0}`],
    }));

  const sortedSetups = sortBySubProductOrder({
    data: mappedSetups || [],
    getProductId: (i) => i.productId || "",
    productRecommendations: productRecommendations,
  });

  const selectedSetups = sortedSetups?.filter(
    (_, index) => !hiddenIdsIndexMap || !hiddenIdsIndexMap?.[index],
  );

  const excludingSubTotals =
    analysis.summary?.hidePortfolioCompositionSubtotals;

  const chunkSelectedSetups = chunk(
    selectedSetups,
    PDFStyleConfigs.CompositionMaxProductsDisplay,
  );

  const selectedSections =
    analysis.summary?.portfolioCompositionByPlatform?.sections?.map(
      (section) => {
        const filteredRows = section.rows?.filter((row) => {
          const { isSubTotal } = row;

          return !excludingSubTotals || !isSubTotal;
        });

        const mappedRows = filteredRows?.map((row) => {
          const mappedCells = row.cells?.map((cell) => ({
            ...cell,
            productId:
              itemProductIdsMap[`${cell.setupId}-${cell.rateCardVersion ?? 0}`],
          }));

          const sortedCells = sortBySubProductOrder({
            data: mappedCells || [],
            getProductId: (cell) => cell.productId || "",
            productRecommendations: productRecommendations,
          });

          return {
            ...row,
            isSingleRow: filteredRows.length === 1,
            cells: sortedCells?.filter(
              (_, index) => !hiddenIdsIndexMap || !hiddenIdsIndexMap?.[index],
            ),
          };
        });

        return {
          ...section,
          rows: mappedRows,
        };
      },
    );

  const chunkSelectedSections: PortfolioCompositionSectionDTO[][] = [];

  const numOfSetups = selectedSetups?.length || 0;

  if (numOfSetups === 0 || !selectedSections) {
    spaceAvailableAfterAppend = handleAppendCompositionTableNoDataSection({
      pages,
      spaceAvailable: spaceAvailableAfterAppend,
      numOfHiddenProducts,
      isReview: analysis.isReview,
    });
  }

  const setupsPerChunk = PDFStyleConfigs.CompositionMaxProductsDisplay;
  const numOfChunks = Math.ceil(numOfSetups / setupsPerChunk);

  for (let i = 0; i < numOfChunks; i++) {
    const startSetupIndex = i * setupsPerChunk;
    const endSetupIndex = startSetupIndex + setupsPerChunk;

    const chunkedSections = selectedSections?.map((section) => {
      const chunkedRows = section.rows?.map((row) => {
        const chunkedCells = row.cells?.slice(startSetupIndex, endSetupIndex);

        return {
          ...row,
          cells: chunkedCells,
        };
      });

      return {
        ...section,
        rows: chunkedRows,
      };
    });

    if (chunkedSections) {
      chunkSelectedSections.push(chunkedSections);
    }
  }

  chunkSelectedSetups.forEach((setupsChunk, index) => {
    const startSetupIndex =
      index * PDFStyleConfigs.CompositionMaxProductsDisplay + 1;
    const endSetupIndex = startSetupIndex + setupsChunk.length - 1;

    spaceAvailableAfterAppend = handleAppendCompositionTable({
      pages,
      spaceAvailable: spaceAvailableAfterAppend,
      startSetupIndex: startSetupIndex,
      endSetupIndex: endSetupIndex,
      isFollowingTable: index > 0,
      isLastTable: index === chunkSelectedSections.length - 1,
      numOfHiddenProducts,
      setups: setupsChunk,
      totalSetup: selectedSetups?.length,
      sections: chunkSelectedSections[index],
      hidePortfolioCompositionSubtotals:
        analysis.summary?.hidePortfolioCompositionSubtotals,
      isReview: analysis.isReview,
      productRecommendations: productRecommendations,
    });
  });

  return (
    spaceAvailableAfterAppend -
    PDFStyleConfigs.CompositionSectionGapHeight -
    (numOfHiddenProducts > 0
      ? PDFStyleConfigs.CompositionHiddenProductsNoteHeight
      : 0)
  );
};

export const portfolioCompositionByPlatformPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  productRecommendations,
  hiddenIdsIndexMap,
  isAppendix,
}: PDFPageHandlerProps) => {
  if (!analysis?.summary?.disqualifiedSmaPortfolioFee) return spaceAvailable;

  let spaceAvailableAfterAppend = spaceAvailable;
  const numOfHiddenProducts = hiddenIdsIndexMap?.filter(Boolean)?.length ?? 0;
  const itemProductIdsMap: Record<string, string> = {};

  analysis?.summary?.smaPortfolioFee?.selectedSubProducts?.forEach((item) => {
    const key = `${item.id}-${item.rateCardVersion ?? 0}`;
    itemProductIdsMap[key] = item.productId;
  });

  const mappedSetups =
    analysis.summary?.disqualifiedPortfolioCompositionByPlatform?.setups?.map(
      (setup) => ({
        ...setup,
        productId:
          itemProductIdsMap[`${setup.id}-${setup.rateCardVersion ?? 0}`],
      }),
    );

  const sortedSetups = sortBySubProductOrder({
    data: mappedSetups || [],
    getProductId: (i) => i.productId || "",
    productRecommendations: productRecommendations,
  });

  const selectedSetups = sortedSetups?.filter(
    (_, index) => hiddenIdsIndexMap || hiddenIdsIndexMap?.[index],
  );

  const excludingSubTotals =
    analysis.summary?.hidePortfolioCompositionSubtotals;

  const chunkSelectedSetups = chunk(
    selectedSetups,
    PDFStyleConfigs.CompositionMaxProductsDisplay,
  );

  const selectedSections =
    analysis.summary?.disqualifiedPortfolioCompositionByPlatform?.sections?.map(
      (section) => {
        const filteredRows = section.rows?.filter((row) => {
          const { isSubTotal } = row;

          return !excludingSubTotals || !isSubTotal;
        });

        const mappedRows = filteredRows?.map((row) => {
          const mappedCells = row.cells?.map((cell) => ({
            ...cell,
            productId:
              itemProductIdsMap[`${cell.setupId}-${cell.rateCardVersion ?? 0}`],
          }));

          const sortedCells = sortBySubProductOrder({
            data: mappedCells || [],
            getProductId: (cell) => cell.productId || "",
            productRecommendations: productRecommendations,
          });

          return {
            ...row,
            isSingleRow: filteredRows.length === 1,
            cells: sortedCells?.filter(
              (_, index) => hiddenIdsIndexMap || hiddenIdsIndexMap?.[index],
            ),
          };
        });

        return {
          ...section,
          rows: mappedRows,
        };
      },
    );

  const chunkSelectedSections: PortfolioCompositionSectionDTO[][] = [];

  const numOfSetups = selectedSetups?.length || 0;

  if (numOfSetups === 0 || !selectedSections) {
    spaceAvailableAfterAppend = handleAppendCompositionTableNoDataSection({
      pages,
      spaceAvailable: spaceAvailableAfterAppend,
      numOfHiddenProducts,
      isReview: analysis.isReview,
    });
  }

  const setupsPerChunk = PDFStyleConfigs.CompositionMaxProductsDisplay;
  const numOfChunks = Math.ceil(numOfSetups / setupsPerChunk);

  for (let i = 0; i < numOfChunks; i++) {
    const startSetupIndex = i * setupsPerChunk;
    const endSetupIndex = startSetupIndex + setupsPerChunk;

    const chunkedSections = selectedSections?.map((section) => {
      const chunkedRows = section.rows?.map((row) => {
        const chunkedCells = row.cells?.slice(startSetupIndex, endSetupIndex);

        return {
          ...row,
          cells: chunkedCells,
        };
      });

      return {
        ...section,
        rows: chunkedRows,
      };
    });

    if (chunkedSections) {
      chunkSelectedSections.push(chunkedSections);
    }
  }

  chunkSelectedSetups.forEach((setupsChunk, index) => {
    const startSetupIndex =
      index * PDFStyleConfigs.CompositionMaxProductsDisplay + 1;
    const endSetupIndex = startSetupIndex + setupsChunk.length - 1;

    spaceAvailableAfterAppend = handleAppendCompositionTable({
      pages,
      spaceAvailable: spaceAvailableAfterAppend,
      startSetupIndex: startSetupIndex,
      endSetupIndex: endSetupIndex,
      isFollowingTable: index > 0,
      isLastTable: index === chunkSelectedSections.length - 1,
      numOfHiddenProducts,
      setups: setupsChunk,
      totalSetup: selectedSetups?.length,
      sections: chunkSelectedSections[index],
      hidePortfolioCompositionSubtotals:
        analysis.summary?.hidePortfolioCompositionSubtotals,
      isReview: analysis.isReview,
      productRecommendations: productRecommendations,
      isAppendix: isAppendix,
    });
  });

  return (
    spaceAvailableAfterAppend -
    PDFStyleConfigs.CompositionSectionGapHeight -
    (numOfHiddenProducts > 0
      ? PDFStyleConfigs.CompositionHiddenProductsNoteHeight
      : 0)
  );
};

export const feesAnalysisPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  feeSubProducts,
  disqualifiedFeeSubProducts,
  hiddenIdsIndexMap,
  disqualifiedHiddenIdsIndexMap,
  hiddenIds,
  disqualifiedHiddenIds,
  productRecommendations,
  isAppendix,
  title,
}: PDFPageHandlerProps) => {
  const hasFeeStep = analysis.summary?.hasFeeStep;
  if (!hasFeeStep) return spaceAvailable;

  const totalPortfolioValue =
    analysis.fee?.feePortfolioDetails?.totalPortfolioValue;

  const isFamilyLevel = isEqual(
    analysis.summary?.feeAnalysis?.shortlisted?.portfolioSetup,
    FeeCalculatorPortfolioLevel.Family,
  );

  const totalSubProductHidden = isAppendix
    ? disqualifiedHiddenIdsIndexMap?.filter((item) => item).length ?? 0
    : hiddenIdsIndexMap?.filter((item) => item).length ?? 0;

  const targetFeeSubProducts = isAppendix
    ? disqualifiedFeeSubProducts
    : feeSubProducts;

  const orderedSubProduct = isAppendix
    ? targetFeeSubProducts
    : sortBySubProductOrder({
        data: targetFeeSubProducts ?? [],
        getProductId: (item) => item.productId,
        productRecommendations: productRecommendations,
      });

  const removedSubProductNameForSpecificProduct =
    removeSubProductNameForSpecificProduct(orderedSubProduct ?? []);

  const subProductsFiltered = removedSubProductNameForSpecificProduct?.filter(
    (subProduct) =>
      isAppendix
        ? !disqualifiedHiddenIds?.includes(subProduct.id)
        : !hiddenIds?.includes(subProduct.id),
  );

  const showRecommendationLabel = isAppendix
    ? false
    : shouldShowRecommendationLabel(productRecommendations);

  const feePerformanceScope = analysis.summary?.excludePerformanceInSMATable
    ? FeesPerformanceScope.Excluding
    : FeesPerformanceScope.Including;

  spaceAvailable = feeEstimatesModeHandler(
    pages,
    spaceAvailable,
    subProductsFiltered ?? [],
    totalSubProductHidden,
    FeeHeatmapMode.Detail,
    true,
    showRecommendationLabel,
    analysis.summary?.feesDisplayStyle,
    isFamilyLevel,
    totalPortfolioValue,
    feePerformanceScope,
    analysis.summary?.excludePerformanceInSMATable,
    analysis.isReview,
    title,
    isAppendix,
  );

  spaceAvailable = feeEstimatesModeHandler(
    pages,
    spaceAvailable,
    subProductsFiltered ?? [],
    totalSubProductHidden,
    FeeHeatmapMode.Projection,
    false,
    showRecommendationLabel,
    analysis.summary?.feesDisplayStyle,
    isFamilyLevel,
    totalPortfolioValue,
    feePerformanceScope,
    analysis.summary?.excludePerformanceInSMATable,
    analysis.isReview,
    title,
    isAppendix,
  );

  if (!analysis.summary?.hideFeeAnalysisGraph) {
    let fullGraphLegendHeight = 0;

    chunk(orderedSubProduct, 5).forEach((items) => {
      let maxLength = 0;
      items.forEach((item) => {
        maxLength = item.name.length > maxLength ? item.name.length : maxLength;
      });
      const MAX_TEXT = 24;
      const countExtraLine = Math.floor(maxLength / MAX_TEXT);

      fullGraphLegendHeight +=
        PDFStyleConfigs.HeightLegendChart +
        countExtraLine * (PDFStyleConfigs.HeightLegendChart / 2);
    });

    const occupyHeight = PDFStyleConfigs.MinHeightChart + fullGraphLegendHeight;
    const isAppend = spaceAvailable >= occupyHeight;

    const groups = groupBy(
      removedSubProductNameForSpecificProduct,
      (obj) => obj.productId,
    );

    const lineChartColors: Record<string, string> = {};
    Object.keys(groups).forEach((key, index) => {
      const group = groups[key];
      const colorRoot =
        PDFStyleConfigs.FeeAnalysisLineChartDefaultColors[index];
      if (group.length === 1) {
        lineChartColors[group[0].id] = hslToHex(
          colorRoot.hue,
          colorRoot.saturation,
          colorRoot.lightness,
        );
      } else {
        const tickColor =
          (PDFStyleConfigs.FeeAnalysisLineChartMaxLightness -
            colorRoot.lightness) /
          group.length;
        const minLightness =
          tickColor >= PDFStyleConfigs.FeeAnalysisLineChartDefaultColorTick
            ? colorRoot.lightness
            : Math.max(
                PDFStyleConfigs.FeeAnalysisLineChartMaxLightness -
                  PDFStyleConfigs.FeeAnalysisLineChartDefaultColorTick *
                    group.length,
                PDFStyleConfigs.FeeAnalysisLineChartMinLightness,
              );

        let maxLightness = Math.min(
          colorRoot.lightness +
            PDFStyleConfigs.FeeAnalysisLineChartDefaultColorTick * 1.5,
          PDFStyleConfigs.FeeAnalysisLineChartMaxLightness,
        );

        if (group.length > 5) {
          maxLightness = Math.min(
            colorRoot.lightness +
              PDFStyleConfigs.FeeAnalysisLineChartDefaultColorTick *
                (group.length - 1),
            PDFStyleConfigs.FeeAnalysisLineChartMaxLightness,
          );
        }

        group.forEach((item, itemIndex) => {
          const lightness = getHSLLightness(
            1,
            group.length,
            itemIndex + 1,
            minLightness,
            maxLightness,
          );
          lineChartColors[item.id] = hslToHex(
            colorRoot.hue,
            colorRoot.saturation,
            lightness,
          );
        });
      }
    });

    pagesHandler(
      pages,
      <View
        style={{
          textAlign: "center",
          justifyContent: "center",
        }}
      >
        <PDFFeeLineChart
          feeSubProducts={subProductsFiltered ?? []}
          chartMode={analysis.summary?.feesDisplayStyle}
          colors={lineChartColors}
          isAppend={isAppend}
          title="Fee analysis (continued)"
          yAxisLegend={
            isFamilyLevel
              ? LineChartLegendConstants.YAxisPlatformAndSMAFees
              : LineChartLegendConstants.YAxisPlatformFees
          }
          isFamilyLevel={isFamilyLevel}
          tablePerformanceScope={feePerformanceScope}
          isExcludePerformanceFees={
            analysis.summary?.excludePerformanceInSMATable
          }
        />

        {chunk(
          subProductsFiltered ?? [],
          PDFStyleConfigs.FeeAnalysisMaxSubProductLegendPerRow,
        ).map((items, index) => (
          <View
            key={index}
            style={{
              textAlign: "center",
              justifyContent: "center",
              flexDirection: "row",
            }}
          >
            <PDFFeeLineChartSubProduct
              subProducts={items}
              colors={lineChartColors}
            />
          </View>
        ))}
      </View>,
      isAppend,
    );

    if (isAppend) {
      return spaceAvailable - occupyHeight;
    }

    return (
      (analysis.isReview
        ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
        : PDFStyleConfigs.PageBodyHeightWithoutHeading) -
      occupyHeight -
      PDFStyleConfigs.TitleHeight -
      PDFStyleConfigs.SubTitleHeight
    );
  }

  return spaceAvailable;
};

export const smaPortfolioPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  hiddenIdsIndexMap,
  disqualifiedHiddenIdsIndexMap,
  smaTableRows,
  disqualifiedSmaTableRows,
  productRecommendations,
  isAppendix,
  title,
}: PDFPageHandlerProps) => {
  const hasFeeStep = analysis.summary?.hasFeeStep;
  if (!hasFeeStep) return spaceAvailable;

  let spaceAvailableAfterAppend = spaceAvailable;

  const numOfHiddenProducts = isAppendix
    ? disqualifiedHiddenIdsIndexMap?.filter(Boolean)?.length ?? 0
    : hiddenIdsIndexMap?.filter(Boolean)?.length ?? 0;

  const allSelectedSubProducts = isAppendix
    ? analysis.summary?.disqualifiedSmaPortfolioFee?.selectedSubProducts
    : analysis.summary?.smaPortfolioFee?.selectedSubProducts;
  const targetHiddenIdsMap = isAppendix
    ? disqualifiedHiddenIdsIndexMap
    : hiddenIdsIndexMap;
  const targetRows = isAppendix ? disqualifiedSmaTableRows : smaTableRows;

  const orderedSubProducts = isAppendix
    ? allSelectedSubProducts || []
    : sortBySubProductOrder({
        data: allSelectedSubProducts ?? [],
        getProductId: (item) => item.productId,
        productRecommendations: productRecommendations,
      });

  const selectedSubProducts = orderedSubProducts.filter(
    (_, index) => !targetHiddenIdsMap || !targetHiddenIdsMap?.[index],
  );

  const orderedRows = isAppendix
    ? targetRows ?? []
    : sortFeatureAnalysisRowsByProductOrder({
        originalRows: targetRows ?? [],
        productRecommendations: productRecommendations,
      });

  const selectedProducts = isAppendix
    ? analysis.summary?.feeAnalysis?.disqualified?.selectedProducts
    : analysis.summary?.feeAnalysis?.shortlisted?.selectedProducts;

  const chunkSelectedSubProducts = chunk(
    selectedSubProducts,
    PDFStyleConfigs.SMAPortfolioMaxProductsDisplay,
  );

  const numOfProducts = selectedSubProducts?.length || 0;

  if (numOfProducts === 0) {
    const entireSectionHeight = PDFStyleConfigs.SMAPortfolioNoDataSectionHeight;
    const shouldAppend = spaceAvailable >= entireSectionHeight;

    const comp = (
      <PDFSMAPortfolio
        isAppending={shouldAppend}
        data={[]}
        selectedSubProducts={[]}
        selectedProducts={[]}
        tableViewMode={analysis.summary?.feesDisplayStyle}
        title={title}
        tablePerformanceScope={
          analysis.summary?.excludePerformanceInSMATable
            ? FeesPerformanceScope.Excluding
            : FeesPerformanceScope.Including
        }
        numOfHiddenProducts={numOfHiddenProducts}
        isReview={analysis.isReview}
        productRecommendations={productRecommendations}
      />
    );

    pagesHandler(pages, comp, shouldAppend);

    spaceAvailableAfterAppend = shouldAppend
      ? spaceAvailable - PDFStyleConfigs.SMAPortfolioTableTitleHeight - 60
      : analysis.isReview
      ? PDFStyleConfigs.PageBodyHeightWithoutHeading
      : PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading -
        PDFStyleConfigs.SMAPortfolioTableTitleHeight -
        60;
  }

  const numOfChunks = Math.ceil(
    numOfProducts / PDFStyleConfigs.SMAPortfolioMaxProductsDisplay,
  );
  const chunkSelectedRows: FeatureAnalysisRowDTO[][] = [];

  const flattenedRows: FeatureAnalysisRowDTO[] = [];
  orderedRows.forEach((row) => {
    flattenedRows.push({
      ...row,
      MAAnalysisData: row.MAAnalysisData?.filter(
        (_, index) => !!targetHiddenIdsMap && !targetHiddenIdsMap?.[index],
      ),
      subRows: [],
    });
    if (row.subRows && row.subRows.length > 0) {
      row.subRows.forEach((subRow) => {
        flattenedRows.push({
          ...subRow,
          MAAnalysisData: subRow.MAAnalysisData?.filter(
            (_, index) => !!targetHiddenIdsMap && !targetHiddenIdsMap?.[index],
          ),
          subRows: [],
        });
        if (subRow.subRows && subRow.subRows.length > 0) {
          subRow.subRows.forEach((subSubRow) => {
            flattenedRows.push({
              ...subSubRow,
              MAAnalysisData: subSubRow.MAAnalysisData?.filter(
                (_, index) =>
                  !!targetHiddenIdsMap && !targetHiddenIdsMap?.[index],
              ),
              subRows: [],
            });
          });
        }
      });
    }
  });

  for (let i = 0; i < numOfChunks; i++) {
    const startSetupIndex = i * PDFStyleConfigs.SMAPortfolioMaxProductsDisplay;
    const endSetupIndex =
      startSetupIndex + PDFStyleConfigs.SMAPortfolioMaxProductsDisplay;

    const chunkedRows = flattenedRows?.map((section) => {
      const chunkedCells = section.MAAnalysisData?.slice(
        startSetupIndex,
        endSetupIndex,
      );

      return {
        ...section,
        MAAnalysisData: chunkedCells,
      };
    });

    if (chunkedRows) {
      chunkSelectedRows.push(chunkedRows);
    }
  }

  chunkSelectedSubProducts.forEach((selectedSubProduct, index) => {
    const startProductIndex =
      index * PDFStyleConfigs.SMAPortfolioMaxProductsDisplay + 1;
    const endProductIndex = startProductIndex + selectedSubProduct.length - 1;

    spaceAvailableAfterAppend = handleAppendSMAPortfolioTable({
      selectedSubProducts: selectedSubProduct,
      selectedProducts: selectedProducts ?? [],
      pages,
      spaceAvailable: spaceAvailableAfterAppend,
      startSliceProductIndex: startProductIndex,
      endSliceProductIndex: endProductIndex,
      isFollowingTable: index > 0,
      isLastTable: index === chunkSelectedSubProducts.length - 1,
      rows: chunkSelectedRows[index],
      tableViewMode: analysis.summary?.feesDisplayStyle,
      tablePerformanceScope: analysis.summary?.excludePerformanceInSMATable
        ? FeesPerformanceScope.Excluding
        : FeesPerformanceScope.Including,
      numOfHiddenProducts: numOfHiddenProducts,
      isReview: analysis.isReview,
      productRecommendations: productRecommendations,
      title: title,
    });
  });

  return (
    spaceAvailableAfterAppend - PDFStyleConfigs.SMAPortfolioSectionGapHeight
  );
};

export const appendixPageHandler = ({
  pages,
  analysis,
}: PDFPageHandlerProps) => {
  pagesHandler(pages, <PDFAppendix />, false);

  return (
    (analysis.isReview
      ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
      : PDFStyleConfigs.PageBodyHeightWithoutHeading) - 50
  );
};

export const platformCommentPageHandler = platformSpecificCommentsPageHandler;

export const appendixFeaturePageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  title,
  isAppendix,
}: PDFPageHandlerProps) => {
  if (
    !analysis.summary?.hasFeatureStep ||
    !analysis.summary.showAppendixFeatureAnalysis
  )
    return spaceAvailable;

  return processAppendixFeatureTable({
    pages: pages,
    spaceAvailable: spaceAvailable,
    analysis: analysis,
    title: title,
    isAppendix: isAppendix,
    disqualifiedData: analysis?.summary?.featureAnalysis?.disqualified,
    isBusinessMetric: false,
  });
};

export const appendixBusinessMetricPageHandler = ({
  pages,
  spaceAvailable,
  analysis,
  title,
  isAppendix,
}: PDFPageHandlerProps) => {
  if (
    !analysis.summary?.hasBusinessMetricStep ||
    !analysis.summary.showAppendixBusinessMetricAnalysis
  )
    return spaceAvailable;

  return processAppendixFeatureTable({
    pages: pages,
    spaceAvailable: spaceAvailable,
    analysis: analysis,
    title: title,
    isAppendix: isAppendix,
    disqualifiedData: analysis?.summary?.businessMetricAnalysis?.disqualified,
    isBusinessMetric: true,
  });
};

// #region private methods

const processFeatureAnalysisSectionData = ({
  spaceAvailable,
  rows,
  isFeature,
  shouldShowRecommendationLabel,
  containsLongProductName,
  isAppendix,
}: {
  spaceAvailable: number;
  rows: FeatureAnalysisRowDTO[];
  isFeature?: boolean;
  shouldShowRecommendationLabel?: boolean;
  containsLongProductName?: boolean;
  isAppendix?: boolean;
}) => {
  const headerHeight =
    shouldShowRecommendationLabel || containsLongProductName
      ? PDFStyleConfigs.TableRowHeaderLineWithRecommendation
      : PDFStyleConfigs.TableRowHeaderLine;
  const remainingHeight = spaceAvailable - headerHeight - (isAppendix ? 30 : 0);

  const { rowCount, totalRowHeight } =
    calculateFeatureAnalysisAvailableRowCount({
      rowsData: rows,
      remainingHeight,
      isFeature,
    });

  let occupyHeight = 0;
  let hasContinue = false;
  let dataSeparated: FeatureAnalysisRowDTO[] = [];

  if (rowCount > 0) {
    if (rows.length > rowCount) {
      dataSeparated = rows.splice(0, rowCount);
      occupyHeight = spaceAvailable;
      hasContinue = true;
    } else if (rows.length <= rowCount) {
      dataSeparated = rows;
      occupyHeight = totalRowHeight + headerHeight;
      hasContinue = false;
    }
  } else {
    hasContinue = true;
  }

  return {
    occupyHeight: occupyHeight,
    data: dataSeparated,
    hasContinue: hasContinue,
  };
};

const calculateFeatureAnalysisAvailableRowCount = ({
  rowsData,
  remainingHeight,
  isFeature,
}: {
  rowsData: FeatureAnalysisRowDTO[];
  remainingHeight: number;
  isFeature?: boolean;
}): {
  rowCount: number;
  totalRowHeight: number;
} => {
  let isIncludedFooterHeight = false;
  let totalRowHeight = 0;
  let rowCount = 0;

  for (const row of rowsData) {
    const { name, longestDataName } = row;

    const isRowNameEqualLongestDataName = name === longestDataName;
    const maxCharsPerLine = row.isEssential
      ? PDFStyleConfigs.TableRowMaxCharsPerLineWithHasEssential
      : !isRowNameEqualLongestDataName
      ? PDFStyleConfigs.TableCellMaxCharsPerLine
      : PDFStyleConfigs.TableRowMaxCharsPerLine;

    const rowHeight =
      (defaultTo(row?.longestDataName?.length, 0) / maxCharsPerLine) *
        PDFStyleConfigs.TableRowSingleLine +
      PDFStyleConfigs.GapSmall;

    const estimatedRowHeight = Math.max(
      Math.max(rowHeight, 1),
      PDFStyleConfigs.TableRowSingleLine,
    );

    if (!isIncludedFooterHeight) {
      const hasFooter = isFeature
        ? row?.isEssential ||
          row.analysisData?.some((item) => item.isMissingEssential)
        : row.analysisData?.some(
            (item) => item.fieldTypeId === FieldTypeId.TextLong,
          );

      if (hasFooter) {
        totalRowHeight += PDFStyleConfigs.TableFooterHeight;
        isIncludedFooterHeight = true;
      }
    }

    totalRowHeight += estimatedRowHeight;

    if (totalRowHeight <= remainingHeight) {
      rowCount++;
    } else {
      break;
    }
  }

  return { rowCount, totalRowHeight };
};

const getElementHeight = (
  node: HTMLElement,
  cellWidth?: string,
  isComment?: boolean,
) => {
  const clone = node.cloneNode(true) as HTMLElement;
  // hide the measured (cloned) element
  clone.style.cssText = `position:fixed;top: -9999px;opacity: 0;width:${cellWidth};padding: 5px;padding-bottom: ${
    isComment ? 5 : 0
  }px;font-size: ${isComment ? 7 : 10}px;line-height: 120%`;
  // add the clone to the DOM
  document.body.appendChild(clone);
  const result = clone.clientHeight;
  // cleanup
  clone.parentNode?.removeChild(clone);

  return result;
};

const dsfParseToHTMLElements = (
  splitElement: Element | null,
  spaceAvailable: number,
  isComment?: boolean,
  cellWidth?: string,
) => {
  if (!splitElement) {
    return;
  }

  const htmlElements: PDFHtmlElement[] = [];
  const parentCloseTag = `</${splitElement.tagName.toLowerCase()}>`;
  const parentOpenTag = parentCloseTag.replace("/", "");

  let contentPerPage = "";
  let totalHeight = 0;
  Array.from(splitElement?.childNodes ?? []).forEach((child, index) => {
    const element = child as Element;
    const nextContentAppended =
      contentPerPage + `${element.outerHTML ?? element.textContent}`;

    const nextHeightAppended =
      getElementHeight(
        createElementNode(nextContentAppended),
        cellWidth,
        isComment,
      ) ?? 0;

    if (spaceAvailable > nextHeightAppended) {
      contentPerPage = nextContentAppended;
      totalHeight = nextHeightAppended;
      if (index === (splitElement?.childNodes?.length ?? 0) - 1) {
        spaceAvailable -= totalHeight;
      }
    } else {
      htmlElements.push({
        content: parentOpenTag + contentPerPage + parentCloseTag,
        height: totalHeight,
      });

      contentPerPage = element.outerHTML ?? element.textContent;
      totalHeight = getElementHeight(
        createElementNode(element.outerHTML ?? element.textContent),
        cellWidth,
        isComment,
      );
      spaceAvailable =
        PDFStyleConfigs.PageBodyHeight -
        ((isComment ? PDFStyleConfigs.CommentTableHeaderHeight : 0) +
          PDFStyleConfigs.TitleHeight +
          10);
    }

    if (index === (splitElement?.childNodes?.length ?? 0) - 1) {
      htmlElements.push({
        content: parentOpenTag + contentPerPage + parentCloseTag,
        height: totalHeight,
      });
    }
  });

  return {
    elements: htmlElements,
    remainingHeight: spaceAvailable,
  };
};

const dsfToBreakBigElements = (node: ChildNode | null) => {
  if (!node) {
    return;
  }

  if (node.nodeType === Node.TEXT_NODE) {
    const nodeContentArray = node.textContent?.trim()?.split(" ");
    if (nodeContentArray && nodeContentArray.length > 4) {
      const breakNodeContents = chunk(nodeContentArray, 4);
      const nodes: Node[] = [];
      breakNodeContents.forEach((content) => {
        const node = document.createElement("span");
        node.textContent = content.join(" ") + " ";
        nodes.push(node);
      });
      node.after(...nodes);
      node.remove();
    }
  } else {
    const element = node as Element;
    Array.from(element.childNodes).map((childNode) =>
      dsfToBreakBigElements(childNode),
    );
  }

  return node as Element;
};

const parseToHTMLElements = (
  elementRef: Element,
  spaceAvailable: number,
  isComment?: boolean,
  isFirstComment?: boolean,
  cellWidth?: string,
) => {
  let htmlElements: PDFHtmlElement[] = [];
  const pageBodyHeight = PDFStyleConfigs.PageBodyHeight;
  let additionalHeight = PDFStyleConfigs.TitleHeight;
  if (isComment) additionalHeight += PDFStyleConfigs.CommentTableHeaderHeight;
  const maxContentHeight = pageBodyHeight - additionalHeight;

  const pushElement = (contentPerPage: string, totalHeight: number) => {
    htmlElements?.push({
      content: contentPerPage,
      height: totalHeight,
    });
  };

  if (elementRef) {
    const { children } = elementRef;
    if (children?.length) {
      let totalHeight = 0;
      let contentPerPage = "";
      let isHandleBigElement = false;
      for (let index = 0; index < children.length; index++) {
        const elementHeight =
          children.item(index)?.getBoundingClientRect()?.height ?? 0;

        const currentElementHeight = isComment
          ? Math.max(elementHeight, PDFStyleConfigs.MinimumCommentCellHeight)
          : elementHeight;

        if (
          currentElementHeight > spaceAvailable &&
          !isFirstComment &&
          isComment
        ) {
          spaceAvailable = maxContentHeight;
        }

        const currentElementContent =
          children.item(index)?.outerHTML ??
          children.item(index)?.textContent ??
          "";

        if (currentElementHeight < spaceAvailable) {
          totalHeight += currentElementHeight;
          contentPerPage += currentElementContent;
          spaceAvailable -= currentElementHeight;
        } else {
          isHandleBigElement = spaceAvailable > 100 || totalHeight === 0;

          if (isHandleBigElement) {
            if (totalHeight !== 0) {
              pushElement(contentPerPage, totalHeight);
            }
            const cloneNode = children
              .item(index)
              ?.cloneNode(true) as ChildNode;

            if (!isEmpty(htmlElements) && !isComment) {
              spaceAvailable = maxContentHeight;
            }

            const splitElement = dsfToBreakBigElements(cloneNode) ?? null;

            const breakElement = dsfParseToHTMLElements(
              splitElement,
              spaceAvailable,
              isComment,
              cellWidth,
            );

            htmlElements = htmlElements.concat(breakElement?.elements ?? []);
            spaceAvailable = breakElement?.remainingHeight ?? 0;
            totalHeight = 0;
            contentPerPage = "";
          } else {
            pushElement(contentPerPage, totalHeight);
            contentPerPage = currentElementContent;
            totalHeight = currentElementHeight;
            spaceAvailable = maxContentHeight;
          }
        }

        if (index === children.length - 1 && !isHandleBigElement) {
          htmlElements?.push({
            content: contentPerPage,
            height: totalHeight,
          });
          break;
        }
      }

      // Objectives and Concluding Remark section
      if (
        !isComment &&
        htmlElements?.length > 1 &&
        htmlElements?.[0]?.content
      ) {
        for (let i = 0; i < htmlElements.length - 1; i++) {
          const lastIndexOfCloseTag = regexLastIndexOf(
            htmlElements[i].content as string,
            /<\/.+>/,
            undefined,
          );
          if (
            htmlElements[i].content?.substring(lastIndexOfCloseTag) === "</p>"
          ) {
            htmlElements[i].content = insertStringAtSpecificIndex(
              htmlElements[0].content,
              lastIndexOfCloseTag,
              " ...(Continued)",
            );
          } else {
            htmlElements[i].content += "<p>...(Continued)</p>";
          }
        }
      }

      return htmlElements;
    }

    return htmlElements;
  }

  return htmlElements;
};

const createElementNode = (htmlString: string) => {
  const tempElement = document.createElement("div");
  tempElement.innerHTML = htmlString ?? "";

  return tempElement;
};

const parseToHTMLElementsByNode = (
  node: HTMLElement,
  cellWidth: string,
  spaceAvailable: number,
  isComment?: boolean,
  isFirstComment?: boolean,
): PDFCommentDetail => {
  const clone = node.cloneNode(true) as HTMLElement;

  clone.style.cssText = `
    position: fixed;
    top: -9999px;
    opacity: 0;
    width: ${cellWidth};
    padding: ${isComment ? 5 : 0}px;
    padding-bottom: 0px;
    font-size: ${isComment ? 7 : 10}px;
    line-height: 120%;
  `;

  document.body.appendChild(clone);

  const result = parseToHTMLElements(
    clone,
    spaceAvailable,
    isComment,
    isFirstComment,
    cellWidth,
  );

  const height = result.reduce(
    (accumulator, currentValue) => accumulator + (currentValue.height ?? 0),
    0,
  );
  clone.parentNode?.removeChild(clone);

  return {
    elements: result,
    totalHeight: height,
  } as PDFCommentDetail;
};

const regexLastIndexOf = (target: string, regex: RegExp, startPos?: number) => {
  regex = regex.global
    ? regex
    : new RegExp(
        regex.source,
        "g" + (regex.ignoreCase ? "i" : "") + (regex.multiline ? "m" : ""),
      );
  if (typeof startPos == "undefined") {
    startPos = target.length;
  } else if (startPos < 0) {
    startPos = 0;
  }
  const stringToWorkWith = target.substring(0, startPos + 1);
  let lastIndexOf = -1;
  let nextStop = 0;
  let result;
  while ((result = regex.exec(stringToWorkWith)) != null) {
    lastIndexOf = result.index;
    regex.lastIndex = ++nextStop;
  }

  return lastIndexOf;
};

const insertStringAtSpecificIndex = (
  target: string,
  index: number,
  valueAdded: string,
): string => {
  return target.substring(0, index) + valueAdded + target.substring(index);
};

const expandHTMLTagInPDF = (htmlString: string) => {
  if (isEmpty(htmlString)) return htmlString;

  return htmlString?.replaceAll("</p><p>", "<br>");
};

const getWhatsIncludedDescription = (
  analysis: AnalysisDTO,
  analysisSteps?: AnalysisStepDTO,
) => {
  const steps = analysisSteps?.steps ?? [];
  const hasBusinessMetric = steps.includes(AnalysisStep.BusinessMetric);
  const hasFee = steps.includes(AnalysisStep.Fee);

  const summaryData = {
    totalPlatform: analysis.summary?.selectedProducts?.length,
    feature: {
      total: analysis.summary?.featureAnalysis?.all?.overallScore?.totalFeature,
      selected:
        analysis.summary?.featureAnalysis?.all?.overallScore
          ?.totalSelectedFeature,
    },
    businessMetric: {
      total:
        analysis.summary?.businessMetricAnalysis?.all?.overallScore
          ?.totalFeature,
      selected:
        analysis.summary?.businessMetricAnalysis?.all?.overallScore
          ?.totalSelectedFeature,
    },
    fee: {
      portfolioSetup: analysis.summary?.feeAnalysis?.all?.portfolioSetup,
    },
  };

  const parts: string[] = [];

  parts.push(
    `This Suitability Review assesses ${summaryData.totalPlatform} ${
      (summaryData.totalPlatform ?? 0) === 1 ? "platform" : "platforms"
    }`,
  );

  parts.push(
    `based on ${summaryData.feature.selected} of ${summaryData.feature.total} features`,
  );

  if (hasBusinessMetric) {
    parts.push(
      `${summaryData.businessMetric.selected} of ${summaryData.businessMetric.total} business metrics`,
    );
  }

  if (hasFee) {
    parts.push(
      `and platform ${
        summaryData.fee.portfolioSetup === FeeCalculatorPortfolioLevel.Family
          ? "and managed account "
          : " "
      }fees`,
    );
  }

  let sentence = parts.join(", ") + ".";

  const excluded: string[] = [];
  if (!hasBusinessMetric) excluded.push("business metrics");
  if (!hasFee) excluded.push("platform fees");

  if (excluded.length) {
    sentence += ` The ${excluded.join(" and ")} section${
      excluded.length > 1 ? "s are" : " is"
    } excluded from this review.`;
  }

  return sentence;
};

const getHighestCommentHeight = (
  comment: PDFReviewCommentSeparated,
  index: number,
) => {
  return Math.max(
    comment?.featureDetail?.elements?.[index]?.height ?? 0,
    comment?.businessMetricDetail?.elements?.[index]?.height ?? 0,
    comment?.feeDetail?.elements?.[index]?.height ?? 0,
    comment?.summaryDetail?.elements?.[index]?.height ?? 0,
  );
};

const isNoAnyComment = (comment: PDFAnalysisComment): boolean => {
  return (
    !comment.feature &&
    !comment.businessMetric &&
    !comment.fee &&
    !comment.summary
  );
};

const separateComments = (
  commentSeparated: PDFReviewCommentSeparated,
  commentExtracted: PDFAnalysisComment,
  availableHeight: number,
  isAppendix?: boolean,
) => {
  const commentsExtracted: PDFAnalysisComment[] = [];
  const { featureDetail, businessMetricDetail, feeDetail, summaryDetail } =
    commentSeparated;

  const biggestSeparated = Math.max(
    featureDetail?.elements?.length ?? 0,
    businessMetricDetail?.elements?.length ?? 0,
    feeDetail?.elements?.length ?? 0,
    summaryDetail?.elements?.length ?? 0,
  );

  if (biggestSeparated >= 1) {
    for (let index = 0; index < biggestSeparated; index++) {
      const height = getHighestCommentHeight(commentSeparated, index);

      commentsExtracted.push({
        ...commentExtracted,
        feature: featureDetail?.elements?.length
          ? featureDetail?.elements[index]?.content ?? ""
          : null,
        businessMetric: businessMetricDetail?.elements?.length
          ? businessMetricDetail.elements[index]?.content ?? ""
          : null,
        fee: feeDetail?.elements?.length
          ? feeDetail.elements[index]?.content ?? ""
          : null,
        summary: summaryDetail?.elements?.length
          ? summaryDetail.elements[index]?.content ?? ""
          : null,
        productName:
          biggestSeparated > 1 && index > 0
            ? `${commentExtracted.productName} (Continued)`
            : commentExtracted.productName,
        height,
        isContinued: biggestSeparated > 1 && index > 0,
      });

      if (availableHeight > height) {
        availableHeight -= height;
      } else {
        availableHeight =
          PDFStyleConfigs.ReviewPageBodyHeight -
          (PDFStyleConfigs.TitleHeight +
            (isAppendix
              ? PDFStyleConfigs.AppendixCommentTableHeaderHeight
              : PDFStyleConfigs.CommentTableHeaderHeight)) -
          height;
      }
    }
  }

  return {
    commentsExtracted,
    remainingHeight: availableHeight,
  };
};

const extractReviewComments = (
  comments: PDFAnalysisComment[],
  availableHeight: number,
  isAppendix?: boolean,
  analysisStep?: AnalysisStepDTO,
  commentSetting?: SummaryCommentSettingDto,
) => {
  let result: PDFAnalysisComment[] = [];
  const steps = analysisStep?.steps.filter((step) => {
    switch (step) {
      case AnalysisStep.Setup:
        return false;
      case AnalysisStep.Feature:
        return commentSetting?.showFeature;
      case AnalysisStep.BusinessMetric:
        return commentSetting?.showBusinessMetric;
      case AnalysisStep.Fee:
        return commentSetting?.showFee;
      case AnalysisStep.Summary:
        return commentSetting?.showSummary;
      default:
        return true;
    }
  });
  const cellWidth =
    ((isAppendix
      ? 0.19675 * PDFStyleConfigs.TableWidth
      : 0.221 * PDFStyleConfigs.TableWidth) *
      (analysisStep?.steps?.filter((step) => step !== AnalysisStep.Setup)
        .length ?? 1)) /
      (steps?.length ?? 1) +
    "px";

  comments?.forEach((item, index) => {
    const comment = { ...item };
    const isFirstComment = index === 0;

    if (isNoAnyComment(item)) {
      result.push({
        ...comment,
        feature: null,
        businessMetric: null,
        fee: null,
        summary: null,
        height: PDFStyleConfigs.MinimumCommentCellHeight,
      });
      availableHeight -= PDFStyleConfigs.MinimumCommentCellHeight;
    } else {
      const commentSeparated: PDFReviewCommentSeparated = {
        featureDetail: parseToHTMLElementsByNode(
          createElementNode(item.feature ?? ""),
          cellWidth,
          availableHeight,
          true,
          isFirstComment,
        ),
        businessMetricDetail: parseToHTMLElementsByNode(
          createElementNode(item.businessMetric ?? ""),
          cellWidth,
          availableHeight,
          true,
          isFirstComment,
        ),
        feeDetail: parseToHTMLElementsByNode(
          createElementNode(item.fee ?? ""),
          cellWidth,
          availableHeight,
          true,
          isFirstComment,
        ),
        summaryDetail: parseToHTMLElementsByNode(
          createElementNode(item.summary ?? ""),
          cellWidth,
          availableHeight,
          true,
          isFirstComment,
        ),
      };
      const separated = separateComments(
        commentSeparated,
        comment,
        availableHeight,
        isAppendix,
      );
      availableHeight = separated.remainingHeight;
      result = result.concat(separated.commentsExtracted);
    }
  });

  return result;
};

const getFeatureIncludedHeight = (row: AnalysisFeatureIncludeDTO): number => {
  const sectionLines =
    (row?.sectionName ?? "").length / PDFStyleConfigs.TableCellMaxCharsPerLine;
  const subSectionLines =
    (row?.subSectionName ?? "").length /
    PDFStyleConfigs.TableCellMaxCharsPerLine;
  const featureNamesLines =
    (row.features?.map((feature) => feature.featureName)?.join(", ") ?? "")
      .length / PDFStyleConfigs.DefaultMaxCharsPerLine;

  const numOfLines = Math.max(sectionLines, subSectionLines, featureNamesLines);

  return (
    Math.ceil(numOfLines) * PDFStyleConfigs.TableRowHeightLinesIncrement +
    PDFStyleConfigs.GapSmall
  );
};

const processFeatureIncluded = (
  spaceAvailable: number,
  data: AnalysisFeatureIncludeDTO[],
) => {
  const additionalHeight =
    PDFStyleConfigs.TitleHeight + PDFStyleConfigs.CommentTableHeaderHeight;

  spaceAvailable -= additionalHeight;

  let remainingHeight = spaceAvailable;

  let occupyHeight = 0;
  let hasContinue: boolean;
  const dataSeparated: AnalysisFeatureIncludeDTO[] = [];

  data.forEach((feature) => {
    const heightRow = getFeatureIncludedHeight(feature);

    if (remainingHeight - heightRow > 0) {
      dataSeparated?.push(feature);
      remainingHeight -= heightRow;
      occupyHeight = spaceAvailable - remainingHeight;
    } else {
      occupyHeight = spaceAvailable;
    }
  });

  if (dataSeparated?.length < data?.length) {
    data?.splice(0, dataSeparated.length);
    hasContinue = true;
  } else {
    hasContinue = false;
  }

  return {
    name: "Features included",
    occupyHeight: occupyHeight,
    dataSeparated: dataSeparated,
    hasContinue: hasContinue,
  };
};

// #endregion private methods

const getEstimatedRowHeight = ({
  rowName,
  isSubTotal,
  shouldHideSubTotal,
}: {
  rowName: string;
  isSubTotal: boolean;
  shouldHideSubTotal?: boolean;
}): {
  estimatedHeight: number;
  numberOfLines: number;
} => {
  const numberOfLines = Math.ceil(
    rowName.length /
      (isSubTotal || shouldHideSubTotal
        ? PDFStyleConfigs.CompositionSubTotalMaxCharsPerLine
        : PDFStyleConfigs.CompositionDefaultMaxCharsPerLine),
  );

  return {
    estimatedHeight:
      PDFStyleConfigs.CompositionDefaultRowHeight +
      PDFStyleConfigs.CompositionRowHeightLinesMultiplier * (numberOfLines - 1),
    numberOfLines: numberOfLines,
  };
};

const getSelectedRowsHeight = ({
  startCursorIndex,
  rowFlatMap,
  spaceAvailable = PDFStyleConfigs.PageBodyHeightWithoutHeading,
  shouldHideSubTotal,
  showRecommendationLabel,
}: {
  startCursorIndex: number;
  rowFlatMap: PortfolioCompositionRowDTO[];
  spaceAvailable?: number;
  shouldHideSubTotal?: boolean;
  isReview?: boolean;
  showRecommendationLabel?: boolean;
}): {
  sumHeight: number;
  endIndex: number;
  belowMinimumInvestmentSize: boolean;
} => {
  let rowSumHeightLocal =
    PDFStyleConfigs.CompositionTableHeaderHeight +
    PDFStyleConfigs.CompositionTableTitleHeight +
    (showRecommendationLabel
      ? PDFStyleConfigs.TableRowRecommendationHeight
      : 0);
  let currentCursorIndex = startCursorIndex;
  let belowMinimumInvestmentSize = false;

  while (rowSumHeightLocal < spaceAvailable) {
    const row = rowFlatMap[currentCursorIndex];

    if (!row) break;

    const containBelowInvestment = row.cells?.some(
      (cell) => cell.belowMinimumInvestmentSize,
    );
    if (containBelowInvestment) belowMinimumInvestmentSize = true;

    const rowName = row.name || "";
    const { estimatedHeight, numberOfLines } = getEstimatedRowHeight({
      rowName,
      isSubTotal: Boolean(row.isSubTotal),
      shouldHideSubTotal,
    });
    const isLastRow = currentCursorIndex === rowFlatMap.length - 1;
    const shouldIncludeLastRow = isLastRow && numberOfLines === 1;

    if (
      rowSumHeightLocal + estimatedHeight > spaceAvailable &&
      !shouldIncludeLastRow
    )
      break;

    rowSumHeightLocal += estimatedHeight;
    currentCursorIndex++;
  }

  return {
    sumHeight: rowSumHeightLocal,
    endIndex: currentCursorIndex,
    belowMinimumInvestmentSize,
  };
};

const handleAppendCompositionTableNoDataSection = ({
  pages,
  spaceAvailable,
  numOfHiddenProducts,
  isReview,
}: {
  pages: PageTemplate[];
  spaceAvailable: number;
  numOfHiddenProducts: number;
  isReview?: boolean;
}) => {
  const entireSectionHeight =
    PDFStyleConfigs.CompositionTableTitleHeight +
    PDFStyleConfigs.CompositionNoDataNoteHeight +
    PDFStyleConfigs.CompositionHiddenProductsNoteHeight;
  const shouldAppend = spaceAvailable >= entireSectionHeight;
  const comp = (
    <PDFPortfolioComposition
      numOfHiddenProducts={numOfHiddenProducts}
      isAppending={shouldAppend}
      totalSetup={0}
      setups={[]}
    />
  );

  pagesHandler(pages, comp, shouldAppend);

  return shouldAppend
    ? spaceAvailable -
        PDFStyleConfigs.CompositionTableTitleHeight -
        PDFStyleConfigs.CompositionNoDataNoteHeight
    : isReview
    ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
    : PDFStyleConfigs.PageBodyHeightWithoutHeading -
      PDFStyleConfigs.SubTitleHeight -
      PDFStyleConfigs.CompositionNoDataNoteHeight;
};

const handleAppendCompositionTable = ({
  pages,
  startSetupIndex,
  endSetupIndex,
  spaceAvailable,
  isFollowingTable,
  isLastTable,
  numOfHiddenProducts,
  setups,
  totalSetup,
  sections,
  hidePortfolioCompositionSubtotals,
  isReview,
  productRecommendations,
  isAppendix,
}: {
  pages: PageTemplate[];
  numOfHiddenProducts: number;
  startSetupIndex: number;
  endSetupIndex: number;
  spaceAvailable: number;
  isFollowingTable?: boolean;
  isLastTable?: boolean;
  setups?: PortfolioCompositionSetupDTO[];
  totalSetup: number;
  sections?: PortfolioCompositionSectionDTO[];
  hidePortfolioCompositionSubtotals?: boolean;
  isReview?: boolean;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
  isAppendix?: boolean;
}) => {
  const rowFlatMap = sections?.flatMap((section) => section.rows) ?? [];

  let rowCursorIndex = 0;
  let spaceAvailableAfterAppend = spaceAvailable;

  while (rowCursorIndex < rowFlatMap?.length) {
    const startSlice = rowCursorIndex;
    // Determine space available based on whether entire table fits
    const spaceForCalculation = spaceAvailableAfterAppend;
    const { endIndex, belowMinimumInvestmentSize, sumHeight } =
      getSelectedRowsHeight({
        startCursorIndex: rowCursorIndex,
        rowFlatMap,
        spaceAvailable: spaceForCalculation,
        shouldHideSubTotal: hidePortfolioCompositionSubtotals,
        showRecommendationLabel: shouldShowRecommendationLabel(
          productRecommendations,
        ),
      });

    const isEntireTableFit = endIndex === rowFlatMap.length;

    // If table doesn't fit, recalculate with full page height
    const finalCalculation = isEntireTableFit
      ? { endIndex, belowMinimumInvestmentSize, sumHeight }
      : getSelectedRowsHeight({
          startCursorIndex: rowCursorIndex,
          rowFlatMap,
          spaceAvailable: isReview
            ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
            : PDFStyleConfigs.PageBodyHeightWithoutHeading,
          shouldHideSubTotal: hidePortfolioCompositionSubtotals,
          showRecommendationLabel: shouldShowRecommendationLabel(
            productRecommendations,
          ),
        });

    const showNumOfHiddenProducts =
      numOfHiddenProducts > 0 &&
      isLastTable &&
      finalCalculation.endIndex === rowFlatMap.length;

    const portfolioCompositionComp = (
      <PDFPortfolioComposition
        rows={rowFlatMap.slice(startSlice, finalCalculation.endIndex)}
        startSetupIndex={startSetupIndex}
        endSetupIndex={endSetupIndex}
        belowMinimumInvestmentSize={finalCalculation.belowMinimumInvestmentSize}
        isAppending={isEntireTableFit}
        isFollowingTable={isFollowingTable}
        shouldHideSubTotal={hidePortfolioCompositionSubtotals}
        numOfHiddenProducts={
          showNumOfHiddenProducts ? numOfHiddenProducts : undefined
        }
        setups={setups}
        totalSetup={totalSetup}
        sections={sections ?? []}
        productRecommendations={productRecommendations}
        isReview={isReview}
        isAppendix={isAppendix}
      />
    );

    pagesHandler(pages, portfolioCompositionComp, isEntireTableFit);

    rowCursorIndex = finalCalculation.endIndex;
    spaceAvailableAfterAppend =
      (isEntireTableFit
        ? spaceAvailableAfterAppend
        : isReview
        ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
        : PDFStyleConfigs.PageBodyHeightWithoutHeading) -
      finalCalculation.sumHeight -
      (finalCalculation.belowMinimumInvestmentSize
        ? PDFStyleConfigs.CompositionTableFooterHeight
        : 0) -
      PDFStyleConfigs.CompositionTableTitleHeight;
  }

  return spaceAvailableAfterAppend;
};

const processFeeEstimatesData = (
  spaceAvailable: number,
  data: FeeSubProductDTO[],
  heatmapMode?: FeeHeatmapMode,
  showFeeAnalysisText?: boolean,
  showFeeEstimatesText?: boolean,
) => {
  let titleHeight = PDFStyleConfigs.FeeAnalysisDefaultFeeTitleHeight;
  if (showFeeAnalysisText) titleHeight += 25;
  if (showFeeEstimatesText) titleHeight += 17;

  const additionalHeight =
    PDFStyleConfigs.FeeAnalysisMarginHeight +
    PDFStyleConfigs.FeeAnalysisFooterHeight +
    (heatmapMode === FeeHeatmapMode.Projection
      ? PDFStyleConfigs.FeeAnalysisLabelLineProjectionHeight
      : PDFStyleConfigs.FeeAnalysisLabelLineDetailHeight) *
      2 +
    titleHeight;

  const remainingHeight = spaceAvailable - additionalHeight;
  const numberRowAvailable = Math.floor(
    remainingHeight / PDFStyleConfigs.FeeAnalysisRowHeight,
  );

  let occupyHeight = 0;
  let hasContinue = false;
  let dataSeparated: FeeSubProductDTO[] = [];

  if (numberRowAvailable > 0) {
    if (data.length > numberRowAvailable) {
      dataSeparated = data.splice(0, numberRowAvailable);
      occupyHeight = spaceAvailable;
      hasContinue = true;
    } else if (data.length === numberRowAvailable) {
      dataSeparated = data;
      occupyHeight = spaceAvailable;
      hasContinue = false;
    } else if (data.length < numberRowAvailable) {
      dataSeparated = data;
      occupyHeight =
        additionalHeight + data.length * PDFStyleConfigs.FeeAnalysisRowHeight;
      hasContinue = false;
    }
  } else {
    if (numberRowAvailable === 0 && data.length === 0) {
      hasContinue = false;
      occupyHeight = additionalHeight;
    } else {
      hasContinue = true;
    }
  }

  return {
    name: "Fee estimates",
    occupyHeight: occupyHeight,
    dataSeparated: dataSeparated,
    hasContinue: hasContinue,
  };
};

const fittedPreviousSection = (
  pages: PageTemplate[],
  spaceAvailable: number,
  subProducts: FeeSubProductDTO[],
  totalSubProductHidden: number,
  heatmapMode: FeeHeatmapMode,
  showRecommendationLabel: boolean,
  feesDisplayStyle?: FeesDisplayStyle,
  isFamilyLevel?: boolean,
  tablePerformanceScope?: FeesPerformanceScope,
  totalPortfolioValue?: number,
  isExcludePerformanceFees?: boolean,
  isAppendix?: boolean,
) => {
  const subProductData = cloneDeep(subProducts);
  const data = processFeeEstimatesData(
    spaceAvailable,
    subProductData ?? [],
    heatmapMode,
    true,
    true,
  );

  const showFeeEstimatesText = heatmapMode !== FeeHeatmapMode.Projection;

  if (!data.hasContinue && data.occupyHeight) {
    const feeEstimatesComp = (
      <PDFFeeAnalysisHeatMap
        totalSubProductHidden={totalSubProductHidden}
        subProducts={data.dataSeparated ?? []}
        totalPortfolioValue={totalPortfolioValue}
        mode={feesDisplayStyle}
        heatmapMode={heatmapMode}
        showFeeEstimatesText={showFeeEstimatesText}
        isFamilyLevel={isFamilyLevel}
        feesPerformanceScope={tablePerformanceScope}
        isExcludePerformanceFees={isExcludePerformanceFees}
        showRecommendationLabel={showRecommendationLabel}
        isAppendix={isAppendix}
      />
    );
    pagesHandler(pages, feeEstimatesComp, true);

    return data.occupyHeight;
  }

  return -1;
};

const feeEstimatesModeHandler = (
  pages: PageTemplate[],
  spaceAvailable: number,
  subProducts: FeeSubProductDTO[],
  totalSubProductHidden: number,
  heatmapMode: FeeHeatmapMode,
  allowShowFeeEstimatesText: boolean,
  showRecommendationLabel: boolean,
  feesDisplayStyle?: FeesDisplayStyle,
  isFamilyLevel?: boolean,
  totalPortfolioValue?: number,
  tablePerformanceScope?: FeesPerformanceScope,
  isExcludePerformanceFees?: boolean,
  isReview?: boolean,
  title?: string,
  isAppendix?: boolean,
) => {
  const pageBodyHeight = isReview
    ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
    : PDFStyleConfigs.PageBodyHeightWithoutHeading;

  const fittedHeight = fittedPreviousSection(
    pages,
    spaceAvailable,
    subProducts,
    totalSubProductHidden,
    heatmapMode,
    showRecommendationLabel,
    feesDisplayStyle,
    isFamilyLevel,
    tablePerformanceScope,
    totalPortfolioValue,
    isExcludePerformanceFees,
    isAppendix,
  );

  if (fittedHeight !== -1) return spaceAvailable - fittedHeight;

  const feeEstimatesComp = (
    subProducts: FeeSubProductDTO[],
    showFeeAnalysisText?: boolean,
    showFeeEstimatesText?: boolean,
  ) => {
    return (
      <PDFFeeAnalysisHeatMap
        totalSubProductHidden={totalSubProductHidden}
        subProducts={subProducts ?? []}
        totalPortfolioValue={totalPortfolioValue}
        mode={feesDisplayStyle}
        heatmapMode={heatmapMode}
        showFeeAnalysisText={showFeeAnalysisText}
        showFeeEstimatesText={showFeeEstimatesText}
        isFamilyLevel={isFamilyLevel}
        feesPerformanceScope={tablePerformanceScope}
        isExcludePerformanceFees={isExcludePerformanceFees}
        showRecommendationLabel={showRecommendationLabel}
        title={title}
        isAppendix={isAppendix}
      />
    );
  };

  const isAppend = spaceAvailable > pageBodyHeight / 2;

  spaceAvailable = isAppend ? spaceAvailable : pageBodyHeight;

  const subProductData = cloneDeep(subProducts);
  const showFeeAnalysisText = !isAppend;
  const showFeeEstimatesText = allowShowFeeEstimatesText;

  let data = processFeeEstimatesData(
    spaceAvailable,
    subProductData ?? [],
    heatmapMode,
    showFeeAnalysisText,
    showFeeEstimatesText,
  );

  pagesHandler(
    pages,
    feeEstimatesComp(
      data?.dataSeparated,
      showFeeAnalysisText,
      showFeeEstimatesText,
    ),
    isAppend,
  );

  while (data.hasContinue) {
    spaceAvailable = pageBodyHeight;

    data = processFeeEstimatesData(
      spaceAvailable,
      subProductData ?? [],
      heatmapMode,
      true,
      false,
    );
    pagesHandler(
      pages,
      feeEstimatesComp(data?.dataSeparated, true, false),
      false,
    );
  }

  return spaceAvailable - data.occupyHeight - PDFStyleConfigs.MarginDefault;
};

const getTickNumber = (num: number): number => {
  // Convert number to string
  const numStr = num.toString();
  // Split string into integer and fractional parts
  const [intPart, fracPart] = numStr.split(".");
  // Calculate the number of digits in the integer part
  const int = Math.abs(parseInt(intPart, 10));
  // Calculate a tick number suitable for that magnitude
  if (int !== 0) {
    const tickNumber = Math.ceil(
      5 / (100 / Math.pow(10, int.toString().length)),
    );

    return Math.ceil(Math.abs(num) / tickNumber) * tickNumber;
  }
  // If the integer part is 0, count the number of zeros after the decimal point
  let zeroCount = 0;
  for (let i = 0; i < fracPart.length; i++) {
    if (fracPart[i] === "0") {
      zeroCount++;
    } else {
      break;
    }
  }
  // Calculate a temporary divisor based on the number of zeros
  const tempDiv = Math.pow(10, zeroCount + 1);

  // Recursively calculate a tick number using the temporary divisor
  return getTickNumber(num * tempDiv) / tempDiv;
};

const getEstimatedPortfolioTableRowHeight = ({
  rowName,
}: {
  rowName: string;
}): {
  estimatedHeight: number;
  numberOfLines: number;
} => {
  const numberOfLines = Math.ceil(
    rowName.length / PDFStyleConfigs.SMAPortfolioDefaultMaxCharsPerLine,
  );

  return {
    estimatedHeight:
      PDFStyleConfigs.SMAPortfolioDefaultRowHeight +
      PDFStyleConfigs.SMAPortfolioRowHeightLinesMultiplier *
        (numberOfLines - 1),
    numberOfLines: numberOfLines,
  };
};

const getSelectedSMAPortfolioTableRowsHeight = ({
  startCursorIndex,
  rowFlatMap,
  spaceAvailable = PDFStyleConfigs.PageBodyHeightWithoutHeading,
}: {
  startCursorIndex: number;
  rowFlatMap: FeatureAnalysisRowDTO[];
  spaceAvailable?: number;
}): {
  sumHeight: number;
  endIndex: number;
} => {
  let rowSumHeightLocal =
    PDFStyleConfigs.SMAPortfolioTableHeaderHeight +
    PDFStyleConfigs.SMAPortfolioTableTitleHeight;
  let currentCursorIndex = startCursorIndex;

  while (rowSumHeightLocal < spaceAvailable) {
    const row = rowFlatMap[currentCursorIndex];

    if (!row) break;

    const rowName = row.name || "";
    const { estimatedHeight, numberOfLines } =
      getEstimatedPortfolioTableRowHeight({
        rowName,
      });
    const isLastRow = currentCursorIndex === rowFlatMap.length - 1;
    const shouldIncludeLastRow = isLastRow && numberOfLines === 1;

    if (
      rowSumHeightLocal + estimatedHeight > spaceAvailable &&
      !shouldIncludeLastRow
    )
      break;

    rowSumHeightLocal += estimatedHeight;
    currentCursorIndex++;
  }

  return {
    sumHeight: rowSumHeightLocal,
    endIndex: currentCursorIndex,
  };
};

const handleAppendSMAPortfolioTable = ({
  pages,
  rows,
  title,
  selectedSubProducts = [],
  selectedProducts = [],
  startSliceProductIndex,
  endSliceProductIndex,
  spaceAvailable,
  isFollowingTable,
  tableViewMode,
  tablePerformanceScope,
  numOfHiddenProducts = 0,
  isLastTable,
  isReview,
  productRecommendations,
}: {
  pages: PageTemplate[];
  rows: FeatureAnalysisRowDTO[];
  title?: string;
  selectedSubProducts: SelectedSubProductDTO[];
  selectedProducts: SelectedProductDTO[];
  startSliceProductIndex: number;
  endSliceProductIndex: number;
  spaceAvailable: number;
  isFollowingTable?: boolean;
  tableViewMode?: FeesDisplayStyle;
  tablePerformanceScope?: FeesPerformanceScope;
  numOfHiddenProducts?: number;
  isLastTable?: boolean;
  isReview?: boolean;
  productRecommendations?: Record<string, Partial<ProductRecommendationDTO>>;
}) => {
  let rowCursorIndex = 0;
  let spaceAvailableAfterAppend = spaceAvailable;

  while (rowCursorIndex < rows.length) {
    const spaceForCalculation = spaceAvailableAfterAppend;
    const { endIndex, sumHeight } = getSelectedSMAPortfolioTableRowsHeight({
      startCursorIndex: rowCursorIndex,
      rowFlatMap: rows,
      spaceAvailable: spaceForCalculation,
    });

    const isEntireTableFit = endIndex === rows.length;

    const finalCalculation = isEntireTableFit
      ? { endIndex, sumHeight }
      : getSelectedSMAPortfolioTableRowsHeight({
          startCursorIndex: rowCursorIndex,
          rowFlatMap: rows,
          spaceAvailable: isReview
            ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
            : PDFStyleConfigs.PageBodyHeightWithoutHeading,
        });

    const countItemTitle =
      selectedSubProducts?.length === 1
        ? `(Product ${startSliceProductIndex} of ${
            selectedSubProducts?.length ?? 0
          })`
        : `(Products ${startSliceProductIndex}-${endSliceProductIndex} of ${
            selectedSubProducts?.length ?? 0
          })`;

    const showNumOfHiddenProducts =
      numOfHiddenProducts > 0 &&
      isLastTable &&
      finalCalculation.endIndex === rows.length;

    const portfolioCompositionComp = (
      <PDFSMAPortfolio
        title={title}
        isAppending={isEntireTableFit}
        data={rows}
        selectedSubProducts={selectedSubProducts}
        selectedProducts={selectedProducts}
        tableViewMode={tableViewMode}
        tablePerformanceScope={tablePerformanceScope}
        isFollowingTable={isFollowingTable}
        countItemTitle={countItemTitle}
        numOfHiddenProducts={
          showNumOfHiddenProducts ? numOfHiddenProducts : undefined
        }
        isReview={isReview}
        productRecommendations={productRecommendations}
      />
    );

    pagesHandler(pages, portfolioCompositionComp, isEntireTableFit);

    rowCursorIndex = finalCalculation.endIndex;
    spaceAvailableAfterAppend =
      (isEntireTableFit
        ? spaceAvailableAfterAppend
        : isReview
        ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
        : PDFStyleConfigs.PageBodyHeightWithoutHeading) -
      finalCalculation.sumHeight -
      PDFStyleConfigs.SMAPortfolioTableFooterHeight;
  }

  return spaceAvailableAfterAppend;
};

const processAppendixFeatureTable = ({
  pages,
  spaceAvailable,
  analysis,
  title,
  isAppendix,
  disqualifiedData,
  isBusinessMetric,
}: {
  pages: PDFPageTemplate[];
  spaceAvailable: number;
  analysis: AnalysisDTO;
  title?: string;
  isAppendix?: boolean;
  disqualifiedData?: AnalysisFeatureAllDataDTO;
  isBusinessMetric?: boolean;
}) => {
  const isIncludeMainHeader =
    pages.length <= PDFStyleConfigs.MaxPageIndexToShowMainHeader;
  const pageBodyHeight = analysis.isReview
    ? isIncludeMainHeader
      ? PDFStyleConfigs.ReviewPageBodyHeight
      : PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
    : isIncludeMainHeader
    ? PDFStyleConfigs.PageBodyHeight
    : PDFStyleConfigs.PageBodyHeightWithoutHeading;
  const showSubSection =
    analysis.summary?.featureAnalysisTableViewMode !== TableViewMode.Summary;
  const isDetailMode =
    analysis.summary?.featureAnalysisTableViewMode === TableViewMode.Detail;
  const showAnalysisScore = !!analysis.summary?.showAnalysisScore;
  const chunkData = splitFeatureAnalysis(
    PDFStyleConfigs.CompositionMaxProductsDisplay,
    disqualifiedData,
  );

  const disqualifiedProductChunks: SelectedProductDTO[][] = [];
  const disqualifiedTables: FeatureAnalysisRowDTO[][] = [];

  chunkData.forEach((item) => {
    const disqualifiedRows = compileFeatureAnalysisRows({
      sections: item.sections || [],
      overallScore: item.overallScore,
      insertOverallScoreRow: true,
    });

    disqualifiedTables.push(disqualifiedRows);
    disqualifiedProductChunks.push(item.selectedProducts);
  });

  const numOfProducts =
    disqualifiedProductChunks.flatMap((productChunk) => productChunk)?.length ||
    0;

  disqualifiedProductChunks.forEach((selectedProduct, index) => {
    const isAppend =
      (spaceAvailable > pageBodyHeight / 2 &&
        spaceAvailable !== pageBodyHeight &&
        pages.length > 1) ||
      index > 0;

    spaceAvailable = isAppend ? spaceAvailable : pageBodyHeight;

    const startProductIndex =
      index * PDFStyleConfigs.CompositionMaxProductsDisplay + 1;
    const endProductIndex = startProductIndex + selectedProduct.length - 1;

    const filteredRowsByViewMode = isBusinessMetric
      ? disqualifiedTables[index]
      : disqualifiedTables[index]?.map((row) => ({
          ...row,
          subRows: showSubSection
            ? row.subRows?.map((subRow) => ({
                ...subRow,
                subRows: isDetailMode ? subRow.subRows : [],
              }))
            : [],
        }));

    const flattenedRows: FeatureAnalysisRowDTO[] = [];
    filteredRowsByViewMode.forEach((row) => {
      flattenedRows.push({ ...row, subRows: [] });
      if (row.subRows && row.subRows.length > 0) {
        row.subRows.forEach((subRow) => {
          flattenedRows.push({ ...subRow, subRows: [] });
          if (subRow.subRows && subRow.subRows.length > 0) {
            subRow.subRows.forEach((subSubRow) => {
              flattenedRows.push({ ...subSubRow, subRows: [] });
            });
          }
        });
      }
    });

    const containsLongProductName =
      checkHeadersContainsLongProductName(selectedProduct);

    spaceAvailable = handleAppendFeatureTable({
      selectedProducts: selectedProduct,
      pages,
      spaceAvailable: spaceAvailable,
      startSliceProductIndex: startProductIndex,
      endSliceProductIndex: endProductIndex,
      rows: flattenedRows ?? [],
      tableViewMode: analysis.summary?.featureAnalysisTableViewMode,
      isReview: analysis.isReview,
      showAnalysisScore: showAnalysisScore,
      isAppendix: isAppendix,
      numOfProducts: numOfProducts,
      title: title,
      containsLongProductName: containsLongProductName,
      isAppend: isAppend,
      isBusinessMetric: isBusinessMetric,
    });
  });

  return spaceAvailable;
};

const handleAppendFeatureTable = ({
  pages,
  rows,
  selectedProducts = [],
  startSliceProductIndex,
  endSliceProductIndex,
  spaceAvailable,
  tableViewMode,
  isReview,
  showAnalysisScore,
  title,
  isAppendix,
  numOfProducts,
  containsLongProductName,
  isAppend,
  isBusinessMetric,
}: {
  pages: PageTemplate[];
  rows: FeatureAnalysisRowDTO[];
  selectedProducts: SelectedProductDTO[];
  startSliceProductIndex: number;
  endSliceProductIndex: number;
  spaceAvailable: number;
  tableViewMode?: TableViewMode;
  isReview?: boolean;
  showAnalysisScore?: boolean;
  title?: string;
  isAppendix?: boolean;
  numOfProducts?: number;
  containsLongProductName?: boolean;
  isAppend?: boolean;
  isBusinessMetric?: boolean;
}) => {
  const renderedComponent = ({
    tableRows,
    isExpand,
  }: {
    tableRows: FeatureAnalysisRowDTO[];
    isExpand: boolean;
  }) => (
    <PDFFeatureAnalysis
      selectedProducts={selectedProducts}
      tableRows={tableRows}
      title={title}
      tableViewMode={isBusinessMetric ? TableViewMode.Detail : tableViewMode}
      isExpand={isExpand}
      isReview={isReview}
      hideScore={!showAnalysisScore}
      startIndex={startSliceProductIndex}
      endIndex={endSliceProductIndex}
      isAppendix={isAppendix}
      numOfProducts={numOfProducts}
      isBusinessMetric={isBusinessMetric}
    />
  );

  let sectionProcessed = processFeatureAnalysisSectionData({
    spaceAvailable,
    rows: rows,
    isFeature: true,
    shouldShowRecommendationLabel: false,
    containsLongProductName: containsLongProductName,
    isAppendix: isAppendix,
  });

  pagesHandler(
    pages,
    renderedComponent({ tableRows: sectionProcessed.data, isExpand: false }),
    isAppend,
  );

  while (sectionProcessed.hasContinue && !isEmpty(rows)) {
    spaceAvailable = isReview
      ? PDFStyleConfigs.ReviewPageBodyHeightWithoutHeading
      : PDFStyleConfigs.PageBodyHeightWithoutHeading;

    sectionProcessed = processFeatureAnalysisSectionData({
      spaceAvailable,
      rows: rows,
      shouldShowRecommendationLabel: false,
      containsLongProductName: containsLongProductName,
      isAppendix: isAppendix,
    });

    pagesHandler(
      pages,
      renderedComponent({ tableRows: sectionProcessed.data, isExpand: true }),
      false,
    );
  }

  return (
    spaceAvailable -
    sectionProcessed.occupyHeight -
    PDFStyleConfigs.MarginDefault
  );
};
